// =============================================================================
// ⚓ ANCHORED: 2026-01-27
// Dit component bevat kritieke fixes voor de mijlpalen genereren flow.
// NIET WIJZIGEN zonder documentatie te raadplegen:
// → PROJECT_NOTES/DOELSTELLINGEN_FIXES_2026-01-27.md
//
// Kritieke secties:
// - handleSaveAll(): Robuuste error handling met partial success support
// - milestoneData constructie: Alleen optionele velden toevoegen als ze waarden hebben
// - Alle secondary operations (linking, actions, logging) zijn non-blocking
// =============================================================================

import React, { useState, useMemo, useEffect } from 'react';
import { createMilestone } from '../firebase/milestones.service';
import { createAction } from '../firebase/actions.service';
import { addMilestoneToSession } from '../firebase/sparring.service';
import { logMilestoneCreated } from '../firebase/changelog.service';
import { 
  SparringMessage, 
  Goal, 
  MilestoneCategory as SystemMilestoneCategory,
  ActionPriority,
  ActionType,
  detectActionTypeFromTitle,
  Milestone
} from '../types';
import { getLatestAssessment, getLatestCompleteAssessment } from '../capabilities/capabilityStorage.service';
import { generateMilestones as generateCapabilityMilestones } from '../capabilities/capabilityMilestone.service';
import { CapabilityAssessment, CompleteAssessment, CapabilityDomain, GeneratedMilestone as CapabilityGeneratedMilestone, DISCProfile, WorkstyleProfile, ExtendedCapabilityDecision, CapabilityDecision, CAPABILITY_DOMAIN_LABELS } from '../capabilities/types';
import { generateComprehensiveActions } from '../actionGeneration.service';
import { LearningStyle } from '../assessment/testDefinitions';
import { generateLearningAction } from '../utils/learningStyleActions';
import { loadSelfTestResults } from '../assessment/selfTestStorage.service';
import { WarningIcon, GoalIcon, SettingsIcon, AnalyticsIcon, HourglassIcon, CalendarIcon } from '../../../icons';

interface GeneratedMilestone {
  key: string;
  title: string;
  meaning: string;
  sparringQuestion?: string; // De vraag die leidde tot dit antwoord
  period: string;
  targetDate: string;
  selected: boolean;
  category?: MilestoneCategory; // For logical ordering
}

// Categories for logical milestone ordering
type MilestoneCategory = 'learn' | 'plan' | 'build' | 'grow' | 'goal';

// Keywords for categorizing milestones
const CATEGORY_KEYWORDS: Record<MilestoneCategory, string[]> = {
  learn: [
    'leren', 'cursus', 'training', 'studeren', 'onderzoeken', 'kennis', 
    'certificaat', 'opleiding', 'workshop', 'masterclass', 'lees', 'boek',
    'begrijpen', 'ontdekken', 'verkennen', 'research', 'analyseren'
  ],
  plan: [
    'plan', 'strategie', 'opzetten', 'voorbereiden', 'ontwerpen', 'bepalen',
    'definiëren', 'uitwerken', 'roadmap', 'concept', 'idee', 'bedenken',
    'structuur', 'framework', 'aanpak', 'blauwdruk'
  ],
  build: [
    'bouwen', 'maken', 'lanceren', 'starten', 'creëren', 'ontwikkelen',
    'implementeren', 'uitvoeren', 'live', 'produceren', 'realiseren',
    'oprichten', 'introduceren', 'aanbieden', 'verkopen', 'werven'
  ],
  grow: [
    'groeien', 'uitbreiden', 'schalen', 'verdubbelen', 'vergroten', 
    'optimaliseren', 'verbeteren', 'automatiseren', 'delegeren', 'team',
    'aannemen', 'partnership', 'investering', 'omzet', 'winst'
  ],
  goal: [
    'einddoel', 'ultiem', 'bereiken', 'doel', 'succes', 'resultaat',
    'afgerond', 'voltooid', 'target', 'visie', 'missie'
  ]
};

// Logical order: Learn first, then Plan, Build, Grow, and finally Goal
const CATEGORY_ORDER: MilestoneCategory[] = ['learn', 'plan', 'build', 'grow', 'goal'];

// =============================================================================
// MAPPING: Lokale MilestoneCategory → ActionType
// =============================================================================
const CATEGORY_TO_ACTION_TYPE: Record<MilestoneCategory, ActionType> = {
  learn: 'research',
  plan: 'plan',
  build: 'build',
  grow: 'grow',
  goal: 'validate', // Goals often need validation/celebration
};

/**
 * Determine action priority based on position and category
 * - First actions (index 0) in build/execute categories get high priority
 * - Middle actions get medium priority
 * - Later actions get low priority
 */
function determineActionPriority(index: number, category: MilestoneCategory, totalActions: number): ActionPriority {
  // Build and grow actions are typically higher priority
  if (category === 'build' || category === 'grow') {
    if (index === 0) return 'high';
    if (index === 1) return 'medium';
    return 'low';
  }
  
  // Learn and plan actions are foundational
  if (category === 'learn' || category === 'plan') {
    if (index === 0) return 'medium';
    return 'low';
  }
  
  // Goal validation
  if (category === 'goal') {
    return 'medium';
  }
  
  return 'medium';
}

// =============================================================================
// SMART ACTION GENERATOR
// Generates concrete, specific actions for milestones (NOT copies of milestone titles)
// =============================================================================

interface GeneratedAction {
  title: string;
  description?: string;
  priority?: ActionPriority;
  actionType?: ActionType;
}

/**
 * Action templates per category
 * Each template generates specific, actionable tasks
 */
const ACTION_TEMPLATES: Record<MilestoneCategory, Array<{
  prefix: string;
  patterns: string[];
}>> = {
  learn: [
    { prefix: 'Onderzoek', patterns: ['de beste aanpak voor', 'welke opties er zijn voor', 'hoe anderen dit doen'] },
    { prefix: 'Maak een lijst van', patterns: ['benodigde kennis voor', 'bronnen over', 'experts in'] },
    { prefix: 'Lees', patterns: ['een artikel over', 'documentatie over', 'een case study over'] },
    { prefix: 'Plan', patterns: ['een gesprek met iemand die dit kent', 'tijd in om te leren over', 'een workshop over'] },
  ],
  plan: [
    { prefix: 'Schets', patterns: ['de hoofdlijnen van', 'een concept voor', 'de structuur van'] },
    { prefix: 'Bepaal', patterns: ['de eerste stappen voor', 'wat je nodig hebt voor', 'de prioriteiten voor'] },
    { prefix: 'Maak een overzicht van', patterns: ['taken voor', 'resources voor', 'tijdlijnen voor'] },
    { prefix: 'Definieer', patterns: ['het doel van', 'de scope van', 'de criteria voor'] },
  ],
  build: [
    { prefix: 'Start met', patterns: ['de basis van', 'een prototype voor', 'de eerste versie van'] },
    { prefix: 'Bouw', patterns: ['het fundament voor', 'een MVP van', 'de kernfunctie van'] },
    { prefix: 'Test', patterns: ['je aannames over', 'de eerste versie van', 'met potentiële gebruikers'] },
    { prefix: 'Verzamel feedback op', patterns: ['je concept voor', 'de uitvoering van', 'je aanpak van'] },
  ],
  grow: [
    { prefix: 'Analyseer', patterns: ['wat werkt bij', 'de resultaten van', 'mogelijkheden om te verbeteren'] },
    { prefix: 'Optimaliseer', patterns: ['het proces van', 'de aanpak voor', 'de efficiëntie van'] },
    { prefix: 'Schaal', patterns: ['wat werkt bij', 'de succesvolle onderdelen van', 'je bereik met'] },
    { prefix: 'Automatiseer', patterns: ['repetitieve taken bij', 'het proces van', 'de workflow voor'] },
  ],
  goal: [
    { prefix: 'Evalueer', patterns: ['je voortgang richting', 'wat nog nodig is voor', 'de laatste stappen naar'] },
    { prefix: 'Vier', patterns: ['de behaalde mijlpalen voor', 'je successen met', 'wat je hebt bereikt'] },
    { prefix: 'Reflecteer op', patterns: ['de reis naar', 'wat je hebt geleerd over', 'je groei richting'] },
    { prefix: 'Plan de volgende stap na', patterns: ['het bereiken van', 'de afronding van', 'het succes van'] },
  ],
};

/**
 * Extract core topic from milestone title
 * Removes common prefixes and cleans up the title
 */
function extractCoreTopic(title: string): string {
  // Remove common prefixes
  const prefixesToRemove = [
    'Start:', 'Werk aan:', 'Begin met', 'Focus op', 
    'Het ontwikkelen van', 'Het bouwen van', 'Het maken van',
    'Onderzoek naar', 'Plan voor', 'Strategie voor'
  ];
  
  let cleaned = title;
  for (const prefix of prefixesToRemove) {
    if (cleaned.toLowerCase().startsWith(prefix.toLowerCase())) {
      cleaned = cleaned.substring(prefix.length).trim();
    }
  }
  
  // Limit length for action context
  if (cleaned.length > 50) {
    cleaned = cleaned.substring(0, 50).trim();
    // Don't cut in the middle of a word
    const lastSpace = cleaned.lastIndexOf(' ');
    if (lastSpace > 30) {
      cleaned = cleaned.substring(0, lastSpace);
    }
  }
  
  return cleaned || title;
}

/**
 * Calculate string similarity using Jaccard similarity of words
 * Returns 0.0 (completely different) to 1.0 (identical)
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  const words1 = new Set(str1.toLowerCase().split(/\s+/).filter(w => w.length > 2));
  const words2 = new Set(str2.toLowerCase().split(/\s+/).filter(w => w.length > 2));
  
  if (words1.size === 0 || words2.size === 0) return 0;
  
  const words1Array = Array.from(words1);
  const words2Array = Array.from(words2);
  
  const intersection = new Set(words1Array.filter(w => words2.has(w)));
  const union = new Set([...words1Array, ...words2Array]);
  
  return intersection.size / union.size;
}

/**
 * Extract specific actionable items from goal description
 * Looks for concrete plans, numbers, names, etc.
 */
function extractSpecificActionsFromGoal(
  description: string,
  domain: CapabilityDomain | undefined,
  milestoneTitle: string
): string[] {
  const actions: string[] = [];
  
  if (!description || description.length < 20) return actions;
  
  const lowerDesc = description.toLowerCase();
  const sentences = description.split(/[.!?]+/).filter(s => s.trim().length > 10);
  
  // Domain-specific keyword matching
  const domainKeywords = domain ? getDomainKeywords(domain) : [];
  
  for (const sentence of sentences) {
    const lowerSentence = sentence.toLowerCase();
    
    // Skip if not related to domain (if domain specified)
    if (domainKeywords.length > 0) {
      const isRelated = domainKeywords.some(kw => lowerSentence.includes(kw.toLowerCase()));
      if (!isRelated) continue;
    }
    
    // Look for specific patterns that indicate concrete actions
    
    // Pattern 1: Numbers (users, revenue, downloads, etc.)
    if (/\d+/.test(sentence)) {
      const trimmed = sentence.trim();
      // Extract meaningful part
      if (trimmed.length > 15 && trimmed.length < 100) {
        // Make it action-oriented
        let action = trimmed;
        if (!action.match(/^(Haal|Bereik|Krijg|Maak|Bouw|Lanceer|Start)/i)) {
          action = `Werk naar: ${action}`;
        }
        actions.push(action.charAt(0).toUpperCase() + action.slice(1));
      }
    }
    
    // Pattern 2: Specific tools/platforms mentioned
    const tools = ['shopify', 'wordpress', 'instagram', 'facebook', 'linkedin', 'youtube', 'tiktok', 'google', 'mailchimp'];
    for (const tool of tools) {
      if (lowerSentence.includes(tool)) {
        actions.push(`Zet ${tool.charAt(0).toUpperCase() + tool.slice(1)} account/strategie op`);
        break;
      }
    }
    
    // Pattern 3: Specific people/roles mentioned
    if (lowerSentence.match(/\b(designer|developer|marketeer|consultant|coach|partner|investeerder)\b/)) {
      const role = lowerSentence.match(/\b(designer|developer|marketeer|consultant|coach|partner|investeerder)\b/)?.[0];
      if (role) {
        actions.push(`Zoek en neem contact op met geschikte ${role}`);
      }
    }
    
    // Pattern 4: Budget/Money mentioned
    if (lowerSentence.match(/€\s*\d+|budget|investering/)) {
      const trimmed = sentence.trim();
      if (trimmed.length > 15 && trimmed.length < 100) {
        actions.push(`Plan budget: ${trimmed}`);
      }
    }
  }
  
  return actions.slice(0, 2); // Max 2 from goal description
}

/**
 * Extract actionable phrase from user response in sparring
 * Looks for concrete commitments, plans, or resources mentioned
 */
function extractActionableFromResponse(response: string): string | null {
  if (!response || response.length < 15 || response.length > 300) return null;
  
  const lowerResponse = response.toLowerCase();
  
  // Pattern 1: "Ik ga..." / "Ik wil..." / "Ik zou..." 
  const intentMatch = response.match(/(Ik ga|Ik wil|Ik zou|We gaan|We willen|Ik plan|Ik denk)\s+(.+)/i);
  if (intentMatch && intentMatch[2]) {
    let action = intentMatch[2].trim();
    // Clean up
    if (action.includes('.')) {
      action = action.split('.')[0];
    }
    if (action.length > 10 && action.length < 100) {
      // Remove "Ik" references and make it action-oriented
      action = action.replace(/^(ik|mijn|me|mezelf)\s+/i, '');
      return action.charAt(0).toUpperCase() + action.slice(1);
    }
  }
  
  // Pattern 2: Names mentioned (potential contacts/partners)
  const nameMatch = response.match(/\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/);
  if (nameMatch && nameMatch[1]) {
    return `Neem contact op met ${nameMatch[1]}`;
  }
  
  // Pattern 3: Specific resources mentioned
  if (lowerResponse.includes('cursus') || lowerResponse.includes('training') || lowerResponse.includes('workshop')) {
    const resourceMatch = response.match(/(cursus|training|workshop)\s+['""]?([^.'"",]+)/i);
    if (resourceMatch && resourceMatch[2]) {
      return `Volg ${resourceMatch[1].toLowerCase()}: ${resourceMatch[2].trim()}`;
    }
  }
  
  // Pattern 4: Tools/platforms mentioned
  const toolMatch = response.match(/\b(Shopify|WordPress|Instagram|Facebook|LinkedIn|YouTube|TikTok|Mailchimp|Canva|Figma)\b/i);
  if (toolMatch && toolMatch[1]) {
    return `Gebruik ${toolMatch[1]} voor implementatie`;
  }
  
  // Pattern 5: Budget/money mentioned
  const budgetMatch = response.match(/€\s*(\d+[\d.,]*)/);
  if (budgetMatch) {
    return `Reserveer budget van ${budgetMatch[0]} voor uitvoering`;
  }
  
  // Pattern 6: Time commitments
  if (lowerResponse.match(/\b(deze week|volgende week|morgen|vandaag|maandag|dinsdag|woensdag|donderdag|vrijdag)\b/)) {
    const timeMatch = response.match(/\b(deze week|volgende week|morgen|vandaag|maandag|dinsdag|woensdag|donderdag|vrijdag)\b/i);
    if (timeMatch) {
      return `Start actie ${timeMatch[0]?.toLowerCase()}`;
    }
  }
  
  // Pattern 7: Specific actions mentioned
  const actionVerbs = ['bellen', 'mailen', 'contact opnemen', 'afspraak maken', 'bestellen', 'kopen', 'huren', 'aanmelden'];
  for (const verb of actionVerbs) {
    if (lowerResponse.includes(verb)) {
      // Extract sentence around this verb
      const sentences = response.split(/[.!?]+/);
      for (const sentence of sentences) {
        if (sentence.toLowerCase().includes(verb)) {
          let action = sentence.trim();
          if (action.length > 10 && action.length < 100) {
            // Make it action-oriented (remove "Ik" etc)
            action = action.replace(/^(Ik|Ik ga|Ik wil|Ik zou|We)\s+/i, '');
            return action.charAt(0).toUpperCase() + action.slice(1);
          }
        }
      }
    }
  }
  
  return null;
}

/**
 * Generate specific, concrete actions for a milestone
 * Returns 2-4 unique actions that are NOT copies of the milestone title
 * NOW WITH PRIORITY, ACTION TYPE, AND FULL ASSESSMENT CONTEXT FOR SPECIFICITY
 */
function generateActionsForMilestone(
  milestone: GeneratedMilestone,
  index: number = 0,
  assessmentContext?: {
    extendedDecision?: ExtendedCapabilityDecision;
    goal?: Goal;
    userResponses?: string[];
    completeAssessment?: CompleteAssessment;
    entrepreneurContext?: any;
    domain?: CapabilityDomain;
    learningStyle?: LearningStyle;
  }
): GeneratedAction[] {
  const category = milestone.category || detectCategory(milestone.title);
  const actionType = CATEGORY_TO_ACTION_TYPE[category];
  const actions: GeneratedAction[] = [];
  
  // =============================================================================
  // PRIORITY 1: SPECIFIC ACTIONS FROM EXTENDED DECISION
  // =============================================================================
  if (assessmentContext?.extendedDecision) {
    const ext = assessmentContext.extendedDecision;
    
    // 1.1: First Action → Direct concrete action
    if (ext.firstAction && ext.firstAction.trim().length > 10) {
      // Extract actionable phrase from first action
      let actionTitle = ext.firstAction.trim();
      
      // If it's a full sentence, try to make it more action-oriented
      if (actionTitle.includes('.')) {
        actionTitle = actionTitle.split('.')[0];
      }
      
      // Remove "Ik ga", "Ik wil", "Ik moet" etc
      actionTitle = actionTitle
        .replace(/^(Ik ga |Ik wil |Ik zou |Ik moet |We gaan |We willen )/i, '')
        .trim();
      
      // Capitalize first letter
      actionTitle = actionTitle.charAt(0).toUpperCase() + actionTitle.slice(1);
      
      actions.push({
        title: actionTitle,
        priority: 'high', // First action is always high priority
        actionType: actionType || 'build',
      });
    }
    
    // 1.2: Missing Knowledge → Learning action
    if (ext.missingKnowledge && ext.missingKnowledge.length > 0) {
      // Join array into string
      const knowledge = ext.missingKnowledge.join(', ');
      
      if (knowledge.length > 5) {
        // Create specific learning action (adapted to learning style)
        const learningAction = assessmentContext?.learningStyle 
          ? generateLearningAction(knowledge, assessmentContext.learningStyle)
          : `Volg een online cursus over ${knowledge}`; // Default fallback
        
        actions.push({
          title: learningAction,
          priority: 'medium',
          actionType: 'research',
        });
      }
    }
    
    // 1.3: Biggest Challenge → Action to address it
    if (ext.biggestChallenge && ext.biggestChallenge.trim().length > 10) {
      const challenge = ext.biggestChallenge.trim();
      
      // Create action to tackle the challenge
      let challengeAction = '';
      if (challenge.toLowerCase().includes('tijd')) {
        challengeAction = `Plan vaste tijdslots voor ${extractCoreTopic(milestone.title)}`;
      } else if (challenge.toLowerCase().includes('geld') || challenge.toLowerCase().includes('budget')) {
        challengeAction = `Vraag 3 offertes aan voor ${extractCoreTopic(milestone.title)}`;
      } else if (challenge.toLowerCase().includes('kennis') || challenge.toLowerCase().includes('weet niet')) {
        challengeAction = `Onderzoek beste aanpak voor: ${challenge.substring(0, 50)}`;
      } else {
        challengeAction = `Maak actieplan om te tackelen: ${challenge.substring(0, 50)}`;
      }
      
      actions.push({
        title: challengeAction,
        priority: 'high',
        actionType: actionType || 'plan',
      });
    }
    
    // 1.4: Current Activities → Build on what's already happening
    if (ext.currentActivities && ext.currentActivities.length > 0) {
      const current = ext.currentActivities.join(', ');
      
      if (current.length > 10) {
        const coreTopic = extractCoreTopic(milestone.title);
        
        actions.push({
          title: `Integreer bestaande activiteit '${current.substring(0, 40)}' in ${coreTopic}`,
          priority: 'medium',
          actionType: actionType || 'build',
        });
      }
    }
  }
  
  // =============================================================================
  // PRIORITY 2: SPECIFIC ACTIONS FROM GOAL DESCRIPTION
  // =============================================================================
  if (assessmentContext?.goal?.description && actions.length < 3) {
    const description = assessmentContext.goal.description;
    const domain = assessmentContext.domain;
    
    // Extract specific mentions related to this domain
    const specificActions = extractSpecificActionsFromGoal(description, domain, milestone.title);
    
    for (const specificAction of specificActions) {
      if (actions.length >= 3) break;
      
      actions.push({
        title: specificAction,
        priority: 'high',
        actionType: actionType || 'build',
      });
    }
  }
  
  // =============================================================================
  // PRIORITY 3: SPECIFIC ACTIONS FROM USER RESPONSES (SPARRING)
  // =============================================================================
  if (assessmentContext?.userResponses && actions.length < 3) {
    const relevantResponses = assessmentContext.userResponses.filter(response => 
      response.length > 20 && response.length < 300
    );
    
    const domain = assessmentContext.domain;
    const domainKeywords = domain ? getDomainKeywords(domain) : [];
    
    for (const response of relevantResponses) {
      if (actions.length >= 3) break;
      
      // Check if response is relevant to this milestone/domain
      const isRelevant = domainKeywords.length === 0 || 
        domainKeywords.some(kw => response.toLowerCase().includes(kw.toLowerCase()));
      
      if (isRelevant) {
        const actionFromResponse = extractActionableFromResponse(response);
        if (actionFromResponse) {
          actions.push({
            title: actionFromResponse,
            priority: 'medium',
            actionType: actionType || 'build',
          });
        }
      }
    }
  }
  
  // =============================================================================
  // PRIORITY 4: DISC/WORKSTYLE ADAPTED ACTIONS
  // =============================================================================
  if (assessmentContext?.completeAssessment && actions.length < 3) {
    const disc = assessmentContext.completeAssessment.discProfile;
    const workstyle = assessmentContext.completeAssessment.workstyleProfile;
    const coreTopic = extractCoreTopic(milestone.title);
    
    // High Quick Start → Experimental, fast actions
    if (workstyle?.scores?.quick_start && workstyle.scores.quick_start >= 7) {
      actions.push({
        title: `Experimenteer met snelle test voor ${coreTopic}`,
        priority: 'high',
        actionType: 'validate',
      });
    }
    
    // High Fact Finder → Research actions
    if (workstyle?.scores?.fact_finder && workstyle.scores.fact_finder >= 7) {
      actions.push({
        title: `Verzamel en analyseer data over ${coreTopic}`,
        priority: 'medium',
        actionType: 'research',
      });
    }
    
    // High D (Dominant) → Direct decision actions
    if (disc?.primary === 'red' && disc.scores.red >= 70) {
      actions.push({
        title: `Neem beslissing over aanpak ${coreTopic}`,
        priority: 'high',
        actionType: 'plan',
      });
    }
    
    // High I (Influence) → Social/networking actions
    if (disc?.primary === 'yellow' && disc.scores.yellow >= 70) {
      actions.push({
        title: `Netwerk met experts/partners voor ${coreTopic}`,
        priority: 'medium',
        actionType: 'build',
      });
    }
  }
  
  // =============================================================================
  // FALLBACK: TEMPLATE-BASED ACTIONS (if not enough specific actions)
  // =============================================================================
  if (actions.length < 2) {
    const templates = ACTION_TEMPLATES[category] || ACTION_TEMPLATES['build'];
    const coreTopic = extractCoreTopic(milestone.title);
    const templateOffset = index % templates.length;
    const numActions = Math.min(3 - actions.length, templates.length);
    
    for (let i = 0; i < numActions; i++) {
      const templateIndex = (templateOffset + i) % templates.length;
      const template = templates[templateIndex];
      
      const patternIndex = (index + i) % template.patterns.length;
      const pattern = template.patterns[patternIndex];
      
      const actionTitle = `${template.prefix} ${pattern} ${coreTopic}`;
      
      const cleanedTitle = actionTitle
        .replace(/\s+/g, ' ')
        .replace(/ van het /g, ' ')
        .replace(/ voor het /g, ' voor ')
        .trim();
      
      const isTooSimilar = 
        cleanedTitle.toLowerCase().includes(milestone.title.toLowerCase().substring(0, 20)) ||
        milestone.title.toLowerCase().includes(cleanedTitle.toLowerCase().substring(0, 20));
      
      if (!isTooSimilar) {
        const priority = determineActionPriority(actions.length, category, 3);
        
        actions.push({
          title: cleanedTitle.charAt(0).toUpperCase() + cleanedTitle.slice(1),
          priority,
          actionType,
        });
      }
    }
  }
  
  // Final fallback
  if (actions.length === 0) {
    const coreTopic = extractCoreTopic(milestone.title);
    const fallbackActions: Record<MilestoneCategory, string> = {
      learn: `Verzamel informatie over ${coreTopic}`,
      plan: `Maak een stappenplan voor ${coreTopic}`,
      build: `Zet de eerste stap richting ${coreTopic}`,
      grow: `Evalueer voortgang van ${coreTopic}`,
      goal: `Check de status van ${coreTopic}`,
    };
    actions.push({ 
      title: fallbackActions[category],
      priority: 'medium',
      actionType,
    });
  }
  
  // Limit to max 3-4 actions per milestone
  return actions.slice(0, 4);
}

/**
 * Detect category from milestone title if not set
 */
function detectCategory(title: string): MilestoneCategory {
  const lowerTitle = title.toLowerCase();
  
  for (const category of CATEGORY_ORDER) {
    const keywords = CATEGORY_KEYWORDS[category];
    if (keywords.some(kw => lowerTitle.includes(kw))) {
      return category;
    }
  }
  
  return 'build'; // Default
}

/**
 * Map capability domain to system milestone category
 */
function mapDomainToCategory(domain: CapabilityDomain): SystemMilestoneCategory {
  const mapping: Record<CapabilityDomain, SystemMilestoneCategory> = {
    'strategy': 'richting_keuzes',
    'marketing': 'zichtbaarheid_markt',
    'sales': 'zichtbaarheid_markt',
    'product': 'systemen_structuur',
    'operations': 'systemen_structuur',
    'finance': 'systemen_structuur',
    'team': 'opschaling_hefboom',
  };
  return mapping[domain];
}

/**
 * Personalize milestone based on full entrepreneur profile
 * Adjusts timing, approach, and phrasing based on personality, role, patterns
 */
function personalizeMilestoneWithProfile(
  milestone: GeneratedMilestone,
  discProfile?: DISCProfile,
  workstyleProfile?: WorkstyleProfile,
  rolePreference?: 'doer' | 'thinker' | 'connector' | 'leader',
  patterns?: { focusDirection?: string; processApproach?: string }
): GeneratedMilestone {
  if (!discProfile && !workstyleProfile && !rolePreference && !patterns) return milestone;

  let personalizedMeaning = milestone.meaning;
  let personalizationApplied = false;
  
  // DISC personalization
  if (discProfile) {
    const primary = discProfile.primary;
    
    if (primary === 'red') {
      // Dominant/Red: Focus op resultaten, directheid, snelheid
      personalizedMeaning += ' Je hebt als natuurlijke leider de drive om dit snel en doelgericht aan te pakken.';
      personalizationApplied = true;
    } else if (primary === 'yellow') {
      // Influential/Yellow: Focus op mensen, netwerken, enthousiasme
      personalizedMeaning += ' Gebruik je netwerk en enthousiasme om anderen te betrekken bij dit doel.';
      personalizationApplied = true;
    } else if (primary === 'green') {
      // Steady/Green: Focus op stabiliteit, teamwork, geduld
      personalizedMeaning += ' Neem de tijd om dit stap voor stap op te bouwen met je team.';
      personalizationApplied = true;
    } else if (primary === 'blue') {
      // Conscientious/Blue: Focus op kwaliteit, analyse, precisie
      personalizedMeaning += ' Zorg dat je alle details goed uitwerkt en onderzoekt voordat je start.';
      personalizationApplied = true;
    }
  }

  // Workstyle personalization
  if (workstyleProfile?.scores) {
    const { fact_finder, quick_start, follow_thru, implementor } = workstyleProfile.scores;
    
    // Handle both string and number formats
    if (typeof quick_start === 'string') {
      // String format: 'low' | 'medium' | 'high'
      if (quick_start === 'high') {
        personalizedMeaning += ' Spring er snel op in en experimenteer.';
        personalizationApplied = true;
      } else if (quick_start === 'low') {
        personalizedMeaning += ' Plan dit zorgvuldig uit voordat je begint.';
        personalizationApplied = true;
      }
    } else if (typeof quick_start === 'number') {
      // Number format: 1-10
      if (quick_start >= 7) {
        personalizedMeaning += ' Spring er snel op in en experimenteer.';
        personalizationApplied = true;
      } else if (quick_start <= 3) {
        personalizedMeaning += ' Plan dit zorgvuldig uit voordat je begint.';
        personalizationApplied = true;
      }
    }
    
    if (typeof fact_finder === 'string') {
      if (fact_finder === 'high') {
        personalizedMeaning += ' Verzamel eerst alle benodigde informatie en bronnen.';
        personalizationApplied = true;
      }
    } else if (typeof fact_finder === 'number' && fact_finder >= 7) {
      personalizedMeaning += ' Verzamel eerst alle benodigde informatie en bronnen.';
      personalizationApplied = true;
    }
    
    if (typeof follow_thru === 'string') {
      if (follow_thru === 'high') {
        personalizedMeaning += ' Maak een duidelijk stappenplan en systeem om dit te volgen.';
        personalizationApplied = true;
      }
    } else if (typeof follow_thru === 'number' && follow_thru >= 7) {
      personalizedMeaning += ' Maak een duidelijk stappenplan en systeem om dit te volgen.';
      personalizationApplied = true;
    }
    
    if (typeof implementor === 'string') {
      if (implementor === 'high') {
        personalizedMeaning += ' Pak het hands-on aan met concrete tastbare resultaten.';
        personalizationApplied = true;
      }
    } else if (typeof implementor === 'number' && implementor >= 7) {
      personalizedMeaning += ' Pak het hands-on aan met concrete tastbare resultaten.';
      personalizationApplied = true;
    }
  }

  // ⭐ NEW: Role Preference personalization
  if (rolePreference) {
    if (rolePreference === 'doer') {
      personalizedMeaning += ' Pak het direct hands-on aan.';
      personalizationApplied = true;
    } else if (rolePreference === 'thinker') {
      personalizedMeaning += ' Analyseer grondig en denk het goed door voordat je begint.';
      personalizationApplied = true;
    } else if (rolePreference === 'connector') {
      personalizedMeaning += ' Betrek je netwerk en maak gebruik van je relaties.';
      personalizationApplied = true;
    } else if (rolePreference === 'leader') {
      personalizedMeaning += ' Delegeer waar mogelijk en stuur het proces aan.';
      personalizationApplied = true;
    }
  }

  // ⭐ NEW: Entrepreneur Balance Patterns personalization
  if (patterns) {
    const { focusDirection, processApproach } = patterns;
    
    if (focusDirection === 'intern') {
      // Intern-gericht: Focus op systemen en structuur
      if (milestone.title.toLowerCase().includes('marketing') || milestone.title.toLowerCase().includes('verkoop')) {
        personalizedMeaning += ' Let op: je bent van nature intern gericht. Zorg dat externe acties aansluiten bij je interne systemen.';
        personalizationApplied = true;
      }
    } else if (focusDirection === 'extern') {
      // Extern-gericht: Focus op markt en klanten
      if (milestone.title.toLowerCase().includes('systemen') || milestone.title.toLowerCase().includes('proces')) {
        personalizedMeaning += ' Let op: je bent van nature extern gericht. Bouw systemen die je externe groei ondersteunen.';
        personalizationApplied = true;
      }
    }
    
    if (processApproach === 'plannen') {
      // Plannen: Geef meer voorbereidingstijd
      personalizedMeaning += ' Neem de tijd om dit goed voor te bereiden.';
      personalizationApplied = true;
    } else if (processApproach === 'afronding') {
      // Afronding: Focus op deliverables
      personalizedMeaning += ' Richt je op concrete, afgeronde resultaten.';
      personalizationApplied = true;
    }
  }

  if (personalizationApplied) {
    console.log(`✨ Personalized milestone: "${milestone.title}"`, {
      disc: discProfile?.primary,
      workstyle: workstyleProfile?.scores,
      role: rolePreference,
      patterns
    });
  }

  return {
    ...milestone,
    meaning: personalizedMeaning,
  };
}

/**
 * Categorize a milestone based on its content
 */
function categorizeMilestone(milestone: GeneratedMilestone): MilestoneCategory {
  const content = `${milestone.title} ${milestone.meaning}`.toLowerCase();
  
  // Check each category for keyword matches
  let bestMatch: MilestoneCategory = 'build'; // Default to build
  let maxMatches = 0;
  
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    const matches = keywords.filter(kw => content.includes(kw)).length;
    if (matches > maxMatches) {
      maxMatches = matches;
      bestMatch = category as MilestoneCategory;
    }
  }
  
  return bestMatch;
}

/**
 * Sort milestones in logical order:
 * 1. Learning/Knowledge (first 20% of timeline)
 * 2. Planning/Preparation (20-35% of timeline)
 * 3. Building/Executing (35-60% of timeline)
 * 4. Growing/Scaling (60-90% of timeline)
 * 5. Final Goal (100%)
 */
function sortMilestonesLogically(
  milestones: GeneratedMilestone[],
  durationDays: number,
  startDate: Date,
  goal: Goal
): GeneratedMilestone[] {
  // Categorize each milestone
  const categorized = milestones.map(m => ({
    ...m,
    category: categorizeMilestone(m)
  }));
  
  // Group by category
  const byCategory: Record<MilestoneCategory, GeneratedMilestone[]> = {
    learn: [],
    plan: [],
    build: [],
    grow: [],
    goal: []
  };
  
  for (const m of categorized) {
    byCategory[m.category || 'build'].push(m);
  }
  
  // Define time ranges for each category (as percentage of total duration)
  const categoryRanges: Record<MilestoneCategory, { start: number; end: number }> = {
    learn: { start: 0.05, end: 0.20 },  // First 5-20%
    plan: { start: 0.15, end: 0.35 },   // 15-35%
    build: { start: 0.30, end: 0.60 },  // 30-60%
    grow: { start: 0.55, end: 0.90 },   // 55-90%
    goal: { start: 0.95, end: 1.0 }     // 95-100%
  };
  
  // Redistribute milestones with logical dates
  const result: GeneratedMilestone[] = [];
  
  for (const category of CATEGORY_ORDER) {
    const items = byCategory[category];
    if (items.length === 0) continue;
    
    const range = categoryRanges[category];
    const rangeSize = range.end - range.start;
    
    items.forEach((item, index) => {
      // Distribute within the category's time range
      const position = items.length === 1 
        ? (range.start + range.end) / 2 
        : range.start + (rangeSize * index / (items.length - 1 || 1));
      
      const daysFromStart = Math.round(durationDays * position);
      const targetDate = new Date(startDate.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
      
      // Generate logical period label
      const yearsFromStart = daysFromStart / 365;
      let period: string;
      if (yearsFromStart < 0.1) {
        period = 'Deze week';
      } else if (yearsFromStart < 0.25) {
        period = `Maand ${Math.ceil(yearsFromStart * 12)}`;
      } else if (yearsFromStart < 1) {
        period = `${Math.round(yearsFromStart * 12)} maanden`;
      } else {
        period = `Jaar ${Math.round(yearsFromStart)}`;
      }
      
      result.push({
        ...item,
        period,
        targetDate: targetDate.toISOString().split('T')[0],
        category
      });
    });
  }
  
  // Check if there's ALREADY a milestone about "this week" (in content OR period)
  const weekDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  // Find milestones that mention "deze week" in their title/content
  const weekContentMilestones = result.filter(m => 
    m.title.toLowerCase().includes('deze week') ||
    m.title.toLowerCase().match(/^(start|begin|eerste stap).*week/i) ||
    m.meaning?.toLowerCase().includes('deze week')
  );
  
  // If we found milestones with "deze week" content, fix their period to match
  if (weekContentMilestones.length > 0) {
    // Set the FIRST one to be the "Deze week" period
    weekContentMilestones[0].period = 'Deze week';
    weekContentMilestones[0].targetDate = weekDate.toISOString().split('T')[0];
    
    // Remove the others (they are duplicates of the same "deze week" action)
    for (let i = 1; i < weekContentMilestones.length; i++) {
      const duplicateIndex = result.indexOf(weekContentMilestones[i]);
      if (duplicateIndex > -1) {
        result.splice(duplicateIndex, 1);
      }
    }
  } else {
    // No "deze week" content found - check if there's already a period labeled "Deze week"
    const hasWeekPeriod = result.some(m => m.period === 'Deze week');
    
    if (!hasWeekPeriod && result.length > 0) {
      // ✅ FIX: Add a starter milestone with a UNIQUE, motivating title (not derived from other milestones)
      result.unshift({
        key: 'week_action',
        title: 'Je reis begint hier',
        meaning: `Eerste concrete stap richting: ${goal.title}. Dit is waar je momentum opbouwt!`,
        period: 'Deze week',
        targetDate: weekDate.toISOString().split('T')[0],
        selected: true,
        category: 'plan'
      });
    }
  }
  
  return result;
}

/**
 * Detect the primary domain for a milestone based on title and meaning
 * Returns the domain if found, undefined otherwise
 */
function detectPrimaryDomainFromMilestone(title: string, meaning: string): CapabilityDomain | undefined {
  const combinedText = `${title} ${meaning}`.toLowerCase();
  
  // Check for exact domain label matches first (most reliable)
  const domainLabels: Record<string, CapabilityDomain> = {
    'strategie & richting': 'strategy',
    'marketing & zichtbaarheid': 'marketing',
    'sales & conversie': 'sales',
    'product / dienst': 'product',
    'operatie & systemen': 'operations',
    'financiën': 'finance',
    'team & capaciteit': 'team',
  };
  
  for (const [label, domain] of Object.entries(domainLabels)) {
    if (combinedText.includes(label)) {
      console.log(`🎯 Detected domain from label "${label}": ${domain}`);
      return domain;
    }
  }
  
  // Fallback: keyword matching
  const domainKeywords: Record<CapabilityDomain, string[]> = {
    strategy: ['strategie', 'richting', 'visie', 'plan', 'focus'],
    marketing: ['marketing', 'zichtbaarheid', 'promotie', 'advertentie', 'social media'],
    sales: ['sales', 'verkoop', 'conversie', 'klant', 'lead'],
    product: ['product', 'dienst', 'ontwikkeling', 'feature'],
    operations: ['operatie', 'systemen', 'proces', 'workflow', 'automatisering'],
    finance: ['financiën', 'budget', 'geld', 'kosten', 'omzet'],
    team: ['team', 'capaciteit', 'medewerker', 'aannemen', 'personeel'],
  };
  
  let bestMatch: { domain: CapabilityDomain; count: number } | null = null;
  
  for (const [domain, keywords] of Object.entries(domainKeywords)) {
    const matchCount = keywords.filter(kw => combinedText.includes(kw)).length;
    if (matchCount > 0 && (!bestMatch || matchCount > bestMatch.count)) {
      bestMatch = { domain: domain as CapabilityDomain, count: matchCount };
    }
  }
  
  if (bestMatch) {
    console.log(`🔍 Detected domain from keywords: ${bestMatch.domain} (${bestMatch.count} matches)`);
    return bestMatch.domain;
  }
  
  console.log(`⚠️ No domain detected for milestone: "${title}"`);
  return undefined; // No domain detected - will generate actions for all domains
}

interface SparringMilestoneGeneratorProps {
  goal: Goal;
  ownerId: string;
  sessionId: string;
  messages: SparringMessage[];
  onComplete: (savedCount: number) => void;
  onCancel: () => void;
  onMilestoneCreated?: (milestoneId: string) => void;
  // ⭐ NEW: Full entrepreneur context for personalization
  entrepreneurContext?: {
    // Self Test Results
    rolePreference?: 'doer' | 'thinker' | 'connector' | 'leader';
    knowledgeLevels?: Record<string, string>;
    
    // Capability Assessment
    capabilityGaps?: string[];
    capabilitySolutions?: Record<string, string>;
    
    // DISC & Werkstijl Profiles
    discProfile?: {
      primary: string;
      secondary: string;
      strengths: string[];
      weaknesses: string[];
    };
    workstyleProfile?: {
      scores?: Record<string, 'low' | 'medium' | 'high'>;
      quickStart?: number;
      factFinder?: number;
      followThru?: number;
      implementor?: number;
      primaryStrength?: string;
    };
    assessmentLevel?: 'none' | 'basic' | 'full';
    
    // Pre-sparring Context
    preSparringAnswers?: Record<string, string>;
    
    // Entrepreneur Balance Patterns
    patterns?: {
      focusDirection?: 'intern' | 'extern' | 'balanced';
      processApproach?: 'plannen' | 'afronding' | 'balanced';
    };
    
    // Language Preference
    languageVariant?: 'praktisch' | 'neutraal' | 'reflectief';
  };
}

/**
 * Generate dynamic milestone templates based on goal duration
 * - Short term (< 6 months): weekly/monthly milestones
 * - Medium term (6 months - 2 years): monthly/quarterly milestones  
 * - Long term (> 2 years): yearly milestones
 */
function generateDynamicTemplates(durationDays: number): Array<{
  key: string;
  label: string;
  relativePosition: number;
  stepKeywords: string[];
}> {
  const durationYears = durationDays / 365;
  
  // Long term goals (> 2 years) - distribute per year
  if (durationYears > 2) {
    const years = Math.ceil(durationYears);
    const templates = [];
    
    // Always start with immediate action
    templates.push({
      key: 'week',
      label: 'Deze week',
      relativePosition: 0.01,
      stepKeywords: ['deze week', 'week', 'vandaag', 'eerste stap']
    });
    
    // Determine number of milestones based on duration
    let yearMilestones: number[];
    
    if (years <= 5) {
      // Short long-term (3-5 years): Every year
      // 3 jaar: 1, 2, 3 = 3 milestones + Deze week = 4 totaal
      // 5 jaar: 1, 2, 3, 4, 5 = 5 milestones + Deze week = 6 totaal
      yearMilestones = Array.from({ length: years }, (_, i) => i + 1);
    } else if (years <= 10) {
      // Medium long-term (6-10 years): Every year
      // 10 jaar: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 = 10 milestones + Deze week = 11 totaal
      yearMilestones = Array.from({ length: years }, (_, i) => i + 1);
    } else {
      // Very long-term (> 10 years): Every year for first 5, then every 2 years
      // 15 jaar: 1, 2, 3, 4, 5, 7, 9, 11, 13, 15 = 10 milestones + Deze week = 11 totaal
      // 20 jaar: 1, 2, 3, 4, 5, 7, 9, 11, 13, 15, 17, 19, 20 = 13 milestones + Deze week = 14 totaal
      yearMilestones = [
        ...Array.from({ length: 5 }, (_, i) => i + 1), // First 5 years: 1, 2, 3, 4, 5
        ...Array.from(
          { length: Math.floor((years - 5) / 2) }, 
          (_, i) => 5 + (i + 1) * 2
        ), // Every 2 years after year 5: 7, 9, 11, 13, etc.
        years // Always include final year
      ];
    }
    
    // Remove duplicates and sort
    const uniqueYears = Array.from(new Set(yearMilestones)).sort((a, b) => a - b);
    
    for (const year of uniqueYears) {
      const relPos = year / years;
      templates.push({
        key: `jaar${year}`,
        label: year === years ? 'Einddoel' : `Jaar ${year}`,
        relativePosition: relPos,
        stepKeywords: [`jaar ${year}`, `${year} jaar`, year === years ? 'einddoel' : '']
      });
    }
    
    return templates;
  }
  
  // Medium term goals (6 months - 2 years) - standard distribution
  if (durationYears >= 0.5) {
    return [
      { key: 'week', label: 'Deze week', relativePosition: 0.02, stepKeywords: ['deze week', 'week', 'vandaag'] },
      { key: '1maand', label: 'Over 1 maand', relativePosition: 0.1, stepKeywords: ['1 maand', 'maand'] },
      { key: '3maanden', label: 'Over 3 maanden', relativePosition: 0.25, stepKeywords: ['3 maanden', 'kwartaal'] },
      { key: '6maanden', label: 'Over 6 maanden', relativePosition: 0.5, stepKeywords: ['6 maanden', 'half jaar'] },
      { key: '1jaar', label: durationYears > 1 ? 'Over 1 jaar' : 'Einddoel', relativePosition: durationYears > 1 ? 0.75 : 1.0, stepKeywords: ['1 jaar', '12 maanden'] },
      ...(durationYears > 1 ? [{ key: 'einddoel', label: 'Einddoel', relativePosition: 1.0, stepKeywords: ['einddoel', 'ultieme doel'] }] : []),
    ];
  }
  
  // Short term goals (< 6 months) - weekly milestones
  const weeks = Math.ceil(durationDays / 7);
  const templates = [
    { key: 'week1', label: 'Week 1', relativePosition: 0.1, stepKeywords: ['week 1', 'eerste week'] },
  ];
  
  if (weeks > 2) templates.push({ key: 'week2', label: 'Week 2', relativePosition: 0.2, stepKeywords: ['week 2'] });
  if (weeks > 4) templates.push({ key: 'maand1', label: 'Maand 1', relativePosition: 0.4, stepKeywords: ['maand 1', '4 weken'] });
  if (weeks > 8) templates.push({ key: 'maand2', label: 'Maand 2', relativePosition: 0.6, stepKeywords: ['maand 2', '8 weken'] });
  templates.push({ key: 'einddoel', label: 'Einddoel', relativePosition: 1.0, stepKeywords: ['einddoel', 'klaar'] });
  
  return templates;
}

// =============================================================================
// HELPER FUNCTIONS FOR SPECIFIC MILESTONE GENERATION
// =============================================================================

/**
 * Get domain-specific keywords for matching user responses
 */
function getDomainKeywords(domain: CapabilityDomain): string[] {
  const keywords: Record<CapabilityDomain, string[]> = {
    strategy: ['strategie', 'richting', 'visie', 'plan', 'doel', 'focus'],
    marketing: ['marketing', 'promotie', 'advertentie', 'campagne', 'content', 'social media', 'seo'],
    sales: ['verkoop', 'sales', 'klant', 'lead', 'conversie', 'omzet'],
    product: ['product', 'dienst', 'ontwikkeling', 'feature', 'functionaliteit'],
    operations: ['operatie', 'proces', 'systeem', 'workflow', 'automatisering'],
    finance: ['financiën', 'budget', 'geld', 'kosten', 'omzet', 'winst'],
    team: ['team', 'medewerker', 'collega', 'aannemen', 'werven', 'delegeren'],
  };
  return keywords[domain] || [];
}

/**
 * Extract actions from goal description related to a specific domain
 */
function extractActionsFromDescription(description: string, domain: CapabilityDomain): string[] {
  const domainKeywords = getDomainKeywords(domain);
  const lowerDesc = description.toLowerCase();
  
  // Check if description mentions this domain
  const mentionsDomain = domainKeywords.some(kw => lowerDesc.includes(kw));
  if (!mentionsDomain) return [];
  
  // Extract sentences that mention the domain
  const sentences = description.split(/[.!?]/).filter(s => {
    const lower = s.toLowerCase();
    return domainKeywords.some(kw => lower.includes(kw)) && s.trim().length > 10;
  });
  
  // Extract action verbs and create milestones
  const actions: string[] = [];
  const actionVerbs = ['maken', 'ontwikkelen', 'opzetten', 'starten', 'bouwen', 'creëren', 'uitwerken', 'implementeren'];
  
  sentences.forEach(sentence => {
    for (const verb of actionVerbs) {
      const index = sentence.toLowerCase().indexOf(verb);
      if (index > -1) {
        // Extract from verb to end of sentence or next comma
        const action = sentence.substring(index).split(/[,;]/)[0].trim();
        if (action.length > 15 && action.length < 100) {
          actions.push(action);
          break;
        }
      }
    }
  });
  
  return actions.slice(0, 2); // Max 2 actions per domain
}

/**
 * Correct grammar and spelling in text
 * Improves readability while preserving meaning
 */
function correctGrammarAndSpelling(text: string): string {
  if (!text || text.length < 3) return text;
  
  let corrected = text;
  
  // Common spelling corrections
  const spellingCorrections: Record<string, string> = {
    'zoal': 'zoals',
    'worde': 'worden',
    'letterleijk': 'letterlijk',
    'gramatiica': 'grammatica',
    'correctie': 'correctie',
    'zinnenne': 'zinnen',
    'leerbare': 'leesbare',
    'lezenbare': 'leesbare',
    'aankomende': 'aankomende',
    'verder': 'verder',
    'podcast': 'podcast',
    'opnames': 'opnames',
    'zelfontwikkeling': 'zelfontwikkeling',
    'mijlpaal': 'mijlpaal',
    'verschillend': 'verschillend',
    'operatie': 'operatie',
    'systemen': 'systemen',
    'ontwikkelen': 'ontwikkelen',
    'financiën': 'financiën',
    'capaciteit': 'capaciteit',
  };
  
  // Apply spelling corrections (case-insensitive)
  for (const [wrong, correct] of Object.entries(spellingCorrections)) {
    const regex = new RegExp(`\\b${wrong}\\b`, 'gi');
    corrected = corrected.replace(regex, correct);
  }
  
  // Fix common grammar issues
  // 1. Fix "is" vs "zijn" (singular vs plural)
  corrected = corrected.replace(/\bis\s+(.+?)\s+dat\s+één\b/gi, 'zijn $1 die één');
  
  // 2. Fix missing spaces after punctuation
  corrected = corrected.replace(/([.!?])([A-Za-z])/g, '$1 $2');
  
  // 3. Fix double spaces
  corrected = corrected.replace(/\s+/g, ' ');
  
  // 4. Capitalize first letter
  if (corrected.length > 0) {
    corrected = corrected.charAt(0).toUpperCase() + corrected.slice(1);
  }
  
  // 5. Fix common word order issues
  corrected = corrected.replace(/\bnaar mijn plannen voor\b/gi, 'volgens mijn plannen voor');
  corrected = corrected.replace(/\bhoe lang\s+(.+?)\s+dat is\b/gi, 'hoe lang $1 duurt');
  
  // 6. Fix incomplete sentences (add proper ending if needed)
  if (!corrected.match(/[.!?]$/) && corrected.length > 20) {
    // Only add period if it's a complete thought (has verb)
    if (corrected.match(/\b(is|zijn|wordt|worden|gaat|gaan|doet|doen|heeft|hebben|kan|kunnen|moet|moeten|wil|willen)\b/i)) {
      corrected = corrected.trim() + '.';
    }
  }
  
  return corrected.trim();
}

/**
 * Extract action from user response
 * NOW WITH GRAMMAR CORRECTION
 */
function extractActionFromResponse(response: string): string {
  // Remove conversational fluff
  let cleaned = response
    .replace(/^(ik wil|ik ga|ik moet|mijn plan is|ik denk dat|ik zou)/i, '')
    .trim();
  
  // Apply grammar and spelling correction
  cleaned = correctGrammarAndSpelling(cleaned);
  
  // Take first sentence or first 80 characters
  const firstSentence = cleaned.split(/[.!?]/)[0].trim();
  if (firstSentence.length <= 80) {
    return firstSentence;
  }
  
  return cleaned.substring(0, 77) + '...';
}

/**
 * Generate specific milestone title using all available data
 */
function generateSpecificMilestoneTitle(
  decision: CapabilityDecision | ExtendedCapabilityDecision,
  goal: Goal,
  extendedDecision?: ExtendedCapabilityDecision,
  sparringContext?: {
    userResponses?: string[];
    goalDescription?: string;
  }
): string {
  const domainLabel = CAPABILITY_DOMAIN_LABELS[decision.domain];
  
  // PRIORITEIT 1: Gebruik firstAction uit extendedDecision als die er is
  if (extendedDecision?.firstAction && extendedDecision.firstAction !== 'Nog onbekend') {
    return extendedDecision.firstAction;
  }
  
  // PRIORITEIT 2: Gebruik missingKnowledge voor specifieke titel
  if (extendedDecision?.missingKnowledge && extendedDecision.missingKnowledge.length > 0) {
    const knowledge = extendedDecision.missingKnowledge.slice(0, 2).join(' en ');
    return `${knowledge} ontwikkelen`;
  }
  
  // PRIORITEIT 3: Gebruik biggestChallenge voor context
  if (extendedDecision?.biggestChallenge) {
    const challenge = extendedDecision.biggestChallenge.toLowerCase();
    if (challenge.includes('strategie')) return `${domainLabel} strategie uitwerken`;
    if (challenge.includes('team') || challenge.includes('medewerker')) return `${domainLabel} team opzetten`;
    if (challenge.includes('budget') || challenge.includes('geld')) return `${domainLabel} budgetplan maken`;
    if (challenge.includes('kennis') || challenge.includes('leren')) return `${domainLabel} kennis ontwikkelen`;
  }
  
  // PRIORITEIT 4: Gebruik goal.description en questionnaireAnswers
  if (sparringContext?.goalDescription) {
    const specificActions = extractActionsFromDescription(sparringContext.goalDescription, decision.domain);
    if (specificActions.length > 0) {
      return specificActions[0];
    }
  }
  
  // PRIORITEIT 5: Gebruik sparring user responses
  if (sparringContext?.userResponses) {
    const domainKeywords = getDomainKeywords(decision.domain);
    const relevantResponses = sparringContext.userResponses.filter(r => 
      domainKeywords.some(kw => r.toLowerCase().includes(kw))
    );
    if (relevantResponses.length > 0) {
      return extractActionFromResponse(relevantResponses[0]);
    }
  }
  
  // FALLBACK: Generieke titel (huidige implementatie)
  if (decision.responsibility === 'other') {
    return `${domainLabel} organiseren`;
  }
  
  if (decision.responsibility === 'self' && decision.resolution) {
    switch (decision.resolution) {
      case 'learn':
        return `${domainLabel} ontwikkelen`;
      case 'outsource':
        return `${domainLabel} uitbesteden`;
      case 'hire':
        return `${domainLabel} teamlid aantrekken`;
    }
  }
  
  return domainLabel;
}

/**
 * Enrich milestone meaning with all available context
 */
function enrichMilestoneMeaning(
  milestone: GeneratedMilestone,
  decision: CapabilityDecision | ExtendedCapabilityDecision,
  goal: Goal,
  extendedDecision?: ExtendedCapabilityDecision,
  completeAssessment?: CompleteAssessment
): string {
  const parts: string[] = [];
  
  // Start with existing meaning or goal context
  if (milestone.meaning && milestone.meaning.length > 10) {
    parts.push(milestone.meaning);
  } else if (goal.description) {
    const goalContext = extractActionsFromDescription(goal.description, decision.domain);
    if (goalContext.length > 0) {
      parts.push(`Doel: ${goalContext[0]}`);
    }
  }
  
  // Voeg extended decision data toe
  if (extendedDecision) {
    if (extendedDecision.timeline && extendedDecision.timeline !== 'Geen haast') {
      parts.push(`Termijn: ${extendedDecision.timeline}`);
    }
    if (extendedDecision.budget && extendedDecision.budget !== 'Nog onbekend') {
      parts.push(`Budget: ${extendedDecision.budget}`);
    }
    if (extendedDecision.biggestChallenge) {
      parts.push(`Uitdaging: ${extendedDecision.biggestChallenge}`);
    }
    if (extendedDecision.currentActivities && extendedDecision.currentActivities.length > 0) {
      parts.push(`Huidige activiteiten: ${extendedDecision.currentActivities.slice(0, 3).join(', ')}`);
    }
    if (extendedDecision.missingKnowledge && extendedDecision.missingKnowledge.length > 0) {
      parts.push(`Te leren: ${extendedDecision.missingKnowledge.slice(0, 3).join(', ')}`);
    }
  }
  
  // Voeg DISC/Workstyle context toe (alleen als het relevant is)
  if (completeAssessment?.discProfile && completeAssessment.discProfile.entrepreneurialStrengths.length > 0) {
    // Alleen toevoegen als het relevant is voor dit domein
    const relevantStrength = completeAssessment.discProfile.entrepreneurialStrengths.find(s => 
      getDomainKeywords(decision.domain).some(kw => s.toLowerCase().includes(kw))
    );
    if (relevantStrength) {
      parts.push(`Sterkte: ${relevantStrength}`);
    }
  }
  
  return parts.length > 0 ? parts.join('. ') : milestone.meaning || '';
}

/**
 * Convert capability milestones to sparring milestones format
 * NOW WITH EXTENDED DECISION DATA FOR SPECIFICITY
 */
function convertCapabilityToSparringMilestones(
  capabilityMilestones: CapabilityGeneratedMilestone[],
  goal: Goal,
  startDate: Date,
  durationDays: number,
  extendedDecisions?: ExtendedCapabilityDecision[],
  completeAssessment?: CompleteAssessment,
  userResponses?: string[]
): GeneratedMilestone[] {
  const converted: GeneratedMilestone[] = [];
  
  if (capabilityMilestones.length === 0) return converted;
  
  // Distribute capability milestones evenly across timeline
  const intervalDays = Math.max(7, Math.floor(durationDays / (capabilityMilestones.length + 1)));
  
  capabilityMilestones.forEach((capMilestone, index) => {
    const targetDate = new Date(startDate.getTime() + (index + 1) * intervalDays * 24 * 60 * 60 * 1000);
    
    // Find matching extended decision
    const extendedDecision = extendedDecisions?.find(ed => ed.domain === capMilestone.domain);
    
    // Find matching basic decision for fallback
    const basicDecision = completeAssessment?.decisions?.find(d => d.domain === capMilestone.domain);
    
    // Generate specific title using all available data
    const specificTitle = generateSpecificMilestoneTitle(
      basicDecision || capMilestone as any,
      goal,
      extendedDecision,
      {
        userResponses,
        goalDescription: goal.description
      }
    );
    
    // Create milestone with specific title
    const milestone: GeneratedMilestone = {
      key: `capability_${capMilestone.domain}`,
      title: specificTitle,
      meaning: `Gebaseerd op je capaciteiten assessment voor ${capMilestone.domain}`,
      period: capMilestone.globalPeriod || `Maand ${Math.ceil((index + 1) * intervalDays / 30)}`,
      targetDate: targetDate.toISOString().split('T')[0],
      selected: true,
    };
    
    // Enrich meaning with all context
    milestone.meaning = enrichMilestoneMeaning(
      milestone,
      basicDecision || capMilestone as any,
      goal,
      extendedDecision,
      completeAssessment
    );
    
    // Determine local category for sorting
    if (capMilestone.resolution === 'learn') milestone.category = 'learn';
    else if (capMilestone.resolution === 'hire' || capMilestone.resolution === 'delegate') milestone.category = 'grow';
    else milestone.category = 'build';
    
    converted.push(milestone);
  });
  
  return converted;
}

/**
 * Generate explicit learning milestones based on assessment data
 * Focuses on missingKnowledge, skillRatings, and challenges
 */
function generateLearningMilestones(
  extendedDecisions: ExtendedCapabilityDecision[],
  completeAssessment: CompleteAssessment | undefined,
  goal: Goal,
  durationDays: number,
  startDate: Date
): GeneratedMilestone[] {
  const learningMilestones: GeneratedMilestone[] = [];
  
  // Collect all missing knowledge across domains
  const allMissingKnowledge: { knowledge: string; domain: CapabilityDomain }[] = [];
  
  for (const decision of extendedDecisions) {
    if (decision.missingKnowledge && decision.missingKnowledge.length > 0) {
      for (const knowledge of decision.missingKnowledge) {
        if (knowledge.trim().length > 5) {
          allMissingKnowledge.push({ knowledge: knowledge.trim(), domain: decision.domain });
        }
      }
    }
  }
  
  // Generate learning milestones from top missing knowledge (max 2-3)
  const topMissingKnowledge = allMissingKnowledge.slice(0, 3);
  
  for (let i = 0; i < topMissingKnowledge.length; i++) {
    const { knowledge, domain } = topMissingKnowledge[i];
    const domainLabel = CAPABILITY_DOMAIN_LABELS[domain] || domain;
    
    // Place learning milestones early in the timeline (first 20-30% of goal duration)
    const learningPhasePosition = 0.1 + (i * 0.1); // 10%, 20%, 30%
    const targetDays = Math.round(durationDays * learningPhasePosition);
    const targetDate = new Date(startDate.getTime() + targetDays * 24 * 60 * 60 * 1000);
    
    const learningMilestone: GeneratedMilestone = {
      key: `learn_${domain}_${i}`,
      title: `Leer: ${knowledge}`,
      meaning: `Kennis opdoen over ${knowledge} voor ${domainLabel}. Dit is essentieel voor het bereiken van je doel.`,
      period: i === 0 ? 'Week 2-4' : i === 1 ? 'Maand 2' : 'Maand 3',
      targetDate: targetDate.toISOString().split('T')[0],
      selected: true,
      category: 'learn',
    };
    
    learningMilestones.push(learningMilestone);
  }
  
  // Also check for low skill ratings (< 3) and create learning milestones
  for (const decision of extendedDecisions) {
    if (decision.skillRatings) {
      for (const [skill, rating] of Object.entries(decision.skillRatings)) {
        if (rating <= 3 && learningMilestones.length < 4) {
          const skillLabel = skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const domainLabel = CAPABILITY_DOMAIN_LABELS[decision.domain] || decision.domain;
          
          const targetDays = Math.round(durationDays * (0.15 + learningMilestones.length * 0.1));
          const targetDate = new Date(startDate.getTime() + targetDays * 24 * 60 * 60 * 1000);
          
          // Check if we already have a similar learning milestone
          const isDuplicate = learningMilestones.some(lm => 
            lm.title.toLowerCase().includes(skillLabel.toLowerCase())
          );
          
          if (!isDuplicate) {
            learningMilestones.push({
              key: `learn_skill_${decision.domain}_${skill}`,
              title: `Verbeter vaardigheid: ${skillLabel}`,
              meaning: `${skillLabel} verbeteren voor ${domainLabel}. Huidige niveau: ${rating}/10.`,
              period: `Maand ${Math.ceil(targetDays / 30)}`,
              targetDate: targetDate.toISOString().split('T')[0],
              selected: true,
              category: 'learn',
            });
          }
        }
      }
    }
  }
  
  return learningMilestones;
}

/**
 * Extract milestones from the AI conversation and generate targetDates
 * Uses user responses as the primary source for milestone content
 * NOW ALSO: Integrates capability assessment decisions AND extended decisions for specificity
 */
function generateMilestonesFromConversation(
  messages: SparringMessage[],
  goal: Goal,
  capabilityMilestones?: GeneratedMilestone[],
  assessmentContext?: {
    extendedDecisions?: ExtendedCapabilityDecision[];
    capabilityDecisions?: CapabilityDecision[];
    completeAssessment?: CompleteAssessment;
  }
): GeneratedMilestone[] {
  const milestones: GeneratedMilestone[] = [];
  
  // Calculate goal duration for date distribution
  const startDate = goal.startDate ? new Date(goal.startDate) : new Date();
  const endDate = goal.endDate ? new Date(goal.endDate) : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000);
  const durationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Generate DYNAMIC templates based on goal duration
  const templates = generateDynamicTemplates(durationDays);

  // Collect all messages
  const userMessages = messages.filter(m => m.role === 'user');
  const assistantMessages = messages.filter(m => m.role === 'assistant');
  
  // Get user responses as array of strings for context
  const userResponses = userMessages.map(m => m.content);
  
  // Get the last AI message for summary extraction
  const lastAIMessage = assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1].content : '';
  
  // COMBINEER DRIE BRONNEN:
  // 1. Capability assessment milestones (foundational, structured)
  const baseCapabilityMilestones = capabilityMilestones || [];
  
  // 2. Extract from AI summary (action plan format) - NOW WITH ASSESSMENT CONTEXT
  const summaryMilestones = extractFromSummary(
    lastAIMessage, 
    templates, 
    goal, 
    durationDays, 
    startDate,
    assessmentContext ? {
      extendedDecisions: assessmentContext.extendedDecisions,
      capabilityDecisions: assessmentContext.capabilityDecisions,
      goalDescription: goal.description
    } : undefined
  );
  
  // 3. Extract from user responses (the actual answers given during sparring) - NOW WITH ASSESSMENT CONTEXT
  // Pass full messages array so we can find the corresponding AI question for each user answer
  const userMilestones = extractFromUserResponses(
    userMessages, 
    messages, // Full messages array for question lookup
    templates, 
    goal, 
    durationDays, 
    startDate,
    assessmentContext ? {
      extendedDecisions: assessmentContext.extendedDecisions,
      capabilityDecisions: assessmentContext.capabilityDecisions,
      goalDescription: goal.description
    } : undefined
  );
  
  // Merge AI and user milestones
  const aiMergedMilestones = mergeMilestones(summaryMilestones, userMilestones, templates, goal, durationDays, startDate);
  
  // Combine capability milestones with AI-generated milestones
  // Remove duplicates: if AI milestone overlaps with capability, prefer the enriched AI version
  const allMilestones = [...baseCapabilityMilestones];
  
  // Helper function for better duplicate detection
  const normalizeTitle = (title: string): string => {
    return title.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const isSimilarTitle = (title1: string, title2: string): boolean => {
    const norm1 = normalizeTitle(title1);
    const norm2 = normalizeTitle(title2);
    
    // Exact match after normalization
    if (norm1 === norm2) return true;
    
    // ✅ FIX: More comprehensive prefix removal for duplicate detection
    const removePrefix = (t: string) => t
      .replace(/^(start|begin|eerste stap|werk aan|focus op|het ontwikkelen van|week \d+|maand \d+):\s*/i, '')
      .replace(/\s+(ontwikkelen|leren|opzetten|uitbesteden|verbeteren|creëren|bouwen)$/i, '') // Remove common suffixes too
      .trim();
    const core1 = removePrefix(norm1);
    const core2 = removePrefix(norm2);
    // ✅ FIX: Stricter check - if cores are identical (even short ones), it's a duplicate
    if (core1 === core2 && core1.length > 5) return true;
    
    // Check if one title is contained in the other (for partial matches)
    if (norm1.length > 15 && norm2.length > 15) {
      const shorter = norm1.length < norm2.length ? norm1 : norm2;
      const longer = norm1.length >= norm2.length ? norm1 : norm2;
      // If shorter is 70% contained in longer, consider duplicate
      if (longer.includes(shorter.substring(0, Math.floor(shorter.length * 0.7)))) {
        return true;
      }
    }
    
    // Check word overlap (very strict)
    const words1 = norm1.split(' ').filter(w => w.length > 3);
    const words2 = norm2.split(' ').filter(w => w.length > 3);
    if (words1.length === 0 || words2.length === 0) return false;
    
    const overlap = words1.filter(w => words2.includes(w)).length;
    const minWords = Math.min(words1.length, words2.length);
    // Require at least 4 words OR 80% overlap (very strict)
    return overlap >= 4 || (minWords > 0 && overlap >= Math.ceil(minWords * 0.8));
  };
  
  // ✅ FIX: Track all titles we've seen to prevent duplicates across all sources
  const seenAllTitles = new Set<string>();
  baseCapabilityMilestones.forEach(m => {
    seenAllTitles.add(normalizeTitle(m.title));
  });
  
  console.log(`🔍 Starting duplicate detection. Base capability milestones: ${baseCapabilityMilestones.length}, AI milestones to process: ${aiMergedMilestones.length}`);
  
  for (const aiMilestone of aiMergedMilestones) {
    const aiNormalized = normalizeTitle(aiMilestone.title);
    
    // ✅ FIX: Check against ALL already added milestones using isSimilarTitle
    const isDuplicate = allMilestones.some(existingMilestone => 
      isSimilarTitle(existingMilestone.title, aiMilestone.title)
    );
    
    if (isDuplicate) {
      console.log(`❌ DUPLICATE DETECTED: "${aiMilestone.title}" is similar to an existing milestone`);
      
      // Find the matching milestone and enrich it with AI meaning (if AI has better context)
      const matchIndex = allMilestones.findIndex(existingMilestone => 
        isSimilarTitle(existingMilestone.title, aiMilestone.title)
      );
      
      if (matchIndex >= 0 && aiMilestone.meaning && aiMilestone.meaning.length > 20) {
        console.log(`  ℹ️ Enriching existing milestone with AI context`);
        // Enrich existing milestone with AI-generated meaning (richer context)
        allMilestones[matchIndex] = {
          ...allMilestones[matchIndex],
          meaning: aiMilestone.meaning,
          // Keep the SHORTER title (usually cleaner, e.g. prefer "Strategie & Richting ontwikkelen" over "Start: Strategie & Richting ontwikkelen")
          title: aiMilestone.title.length < allMilestones[matchIndex].title.length ? aiMilestone.title : allMilestones[matchIndex].title,
        };
      }
    } else {
      console.log(`✅ UNIQUE: Adding "${aiMilestone.title}"`);
      allMilestones.push(aiMilestone);
      seenAllTitles.add(aiNormalized);
    }
  }
  
  // =============================================================================
  // ADD EXPLICIT LEARNING MILESTONES FROM ASSESSMENT
  // =============================================================================
  if (assessmentContext?.extendedDecisions || assessmentContext?.completeAssessment) {
    const learningMilestones = generateLearningMilestones(
      assessmentContext.extendedDecisions || [],
      assessmentContext.completeAssessment,
      goal,
      durationDays,
      startDate
    );
    
    // Add learning milestones if they don't duplicate existing ones
    for (const learnMilestone of learningMilestones) {
      const isDuplicate = allMilestones.some(m => isSimilarTitle(m.title, learnMilestone.title));
      if (!isDuplicate) {
        allMilestones.push(learnMilestone);
      }
    }
  }
  
  if (allMilestones.length > 0) {
    // LOGISCHE SORTERING: Analyseer content en plaats in juiste volgorde
    // Leren → Plannen → Bouwen → Groeien → Einddoel
    return sortMilestonesLogically(allMilestones, durationDays, startDate, goal);
  }

  // Fallback: If still no milestones, create defaults based on goal
  const defaultMilestones: GeneratedMilestone[] = [
    { 
      key: 'start', 
      title: `Onderzoek en plan voor ${goal.title}`, 
      meaning: `Begin met het uitwerken van je aanpak`,
      period: 'Maand 1',
      targetDate: new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      selected: true,
      category: 'plan'
    },
    { 
      key: 'build', 
      title: `Start uitvoering ${goal.title}`, 
      meaning: `Eerste concrete stappen zetten`,
      period: 'Jaar 1',
      targetDate: new Date(startDate.getTime() + durationDays * 0.3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      selected: true,
      category: 'build'
    },
    { 
      key: 'grow', 
      title: `Groei en optimalisatie`, 
      meaning: `Uitbreiden en verbeteren van resultaten`,
      period: `Jaar ${Math.ceil(durationDays / 365 * 0.6)}`,
      targetDate: new Date(startDate.getTime() + durationDays * 0.6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      selected: true,
      category: 'grow'
    },
    { 
      key: 'end', 
      title: goal.title, 
      meaning: `Einddoel bereikt`,
      period: 'Einddoel',
      targetDate: endDate.toISOString().split('T')[0],
      selected: true,
      category: 'goal'
    },
  ];

  return defaultMilestones;
}

/**
 * Extract milestones from user responses during sparring
 * Maps user responses to dynamic templates based on message order
 * NOW ENRICHED WITH ASSESSMENT CONTEXT FOR SPECIFICITY
 * NOW ALSO: Captures the AI question that led to each user answer
 */
function extractFromUserResponses(
  userMessages: SparringMessage[],
  allMessages: SparringMessage[], // Full conversation to find AI questions
  templates: Array<{ key: string; label: string; relativePosition: number; stepKeywords: string[] }>,
  goal: Goal,
  durationDays: number,
  startDate: Date,
  assessmentContext?: {
    extendedDecisions?: ExtendedCapabilityDecision[];
    capabilityDecisions?: CapabilityDecision[];
    goalDescription?: string;
  }
): GeneratedMilestone[] {
  const milestones: GeneratedMilestone[] = [];
  const seenTitles = new Set<string>(); // Track titles to prevent duplicates within this function
  
  // Helper for normalization
  const normalizeTitle = (title: string): string => {
    return title.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  // Helper to find the AI question that preceded a user message
  const findPrecedingQuestion = (userMsg: SparringMessage): string | undefined => {
    const userIndex = allMessages.findIndex(m => m.id === userMsg.id);
    if (userIndex <= 0) return undefined;
    
    // Look backwards for the most recent assistant message
    for (let i = userIndex - 1; i >= 0; i--) {
      if (allMessages[i].role === 'assistant') {
        const content = allMessages[i].content;
        // Extract the question part (last question in the message)
        const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
        // Find the last question-like sentence
        for (let j = sentences.length - 1; j >= 0; j--) {
          const sentence = sentences[j].trim();
          // Check if it's a question (ends with ? or contains question words)
          if (sentence.includes('?') || 
              /^(wat|hoe|waar|wanneer|wie|welke|kun|wil|zou|is|zijn|heb)/i.test(sentence)) {
            return sentence + (sentence.endsWith('?') ? '' : '?');
          }
        }
        // If no clear question found, return a truncated version of the AI message
        if (content.length > 50) {
          return content.substring(0, 200) + '...';
        }
        return content;
      }
    }
    return undefined;
  };
  
  // Filter to only messages that could be valid goals
  const meaningfulMessages = userMessages.filter(m => {
    const content = m.content.trim();
    // Skip very short messages
    if (content.length < 15) return false;
    // Skip questions
    if (content.endsWith('?')) return false;
    // Skip pure conversational responses
    if (!isValidTitle(content) && !extractCoreGoal(content)) return false;
    return true;
  });
  
  // Sort templates by relative position for proper ordering
  const sortedTemplates = [...templates].sort((a, b) => a.relativePosition - b.relativePosition);
  
  // Map user messages to templates in order
  let templateIndex = 0;
  for (const msg of meaningfulMessages) {
    if (templateIndex >= sortedTemplates.length) break;
    
    const template = sortedTemplates[templateIndex];
    const userContent = msg.content;
    
    // Try to match user response with assessment data for specificity
    let specificTitle = cleanTitle(userContent, template.label);
    
    // If we have assessment context, try to enrich the title
    if (assessmentContext) {
      // Check if user response mentions a domain
      for (const domain of Object.keys(CAPABILITY_DOMAIN_LABELS) as CapabilityDomain[]) {
        const domainKeywords = getDomainKeywords(domain);
        const mentionsDomain = domainKeywords.some(kw => userContent.toLowerCase().includes(kw));
        
        if (mentionsDomain) {
          // Find matching extended decision
          const extendedDecision = assessmentContext.extendedDecisions?.find(ed => ed.domain === domain);
          const basicDecision = assessmentContext.capabilityDecisions?.find(d => d.domain === domain);
          
          if (extendedDecision || basicDecision) {
            // Generate specific title using assessment data
            const assessmentTitle = generateSpecificMilestoneTitle(
              basicDecision || extendedDecision!,
              goal,
              extendedDecision,
              {
                userResponses: [userContent],
                goalDescription: assessmentContext.goalDescription
              }
            );
            
            // Use assessment title if it's more specific than cleaned user response
            if (assessmentTitle.length > specificTitle.length || 
                (assessmentTitle !== specificTitle && assessmentTitle !== template.label)) {
              specificTitle = assessmentTitle;
            }
            break;
          }
        }
      }
    }
    
    // Skip if we ended up with just the fallback label (no real content)
    if (specificTitle === template.label) {
      continue; // Don't increment template index, try next message
    }
    
    // Check if we already have a similar title (prevent duplicates within this extraction)
    const normalizedTitle = normalizeTitle(specificTitle);
    const isDuplicate = Array.from(seenTitles).some(seenTitle => {
      const similarityCheck = seenTitle === normalizedTitle;
      // Also check if core content is the same (removing "Start:", "Begin met", etc.)
      const removePrefix = (t: string) => t.replace(/^(start|begin|eerste stap|week \d+):\s*/i, '').trim();
      const core1 = removePrefix(seenTitle);
      const core2 = removePrefix(normalizedTitle);
      return similarityCheck || (core1 === core2 && core1.length > 10);
    });
    
    if (isDuplicate) {
      continue; // Skip this duplicate, try next message
    }
    
    // Calculate target date
    const daysFromStart = Math.round(durationDays * template.relativePosition);
    const targetDate = new Date(startDate.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
    
    // Apply grammar correction to title and meaning
    const correctedTitle = correctGrammarAndSpelling(specificTitle);
    const correctedMeaning = correctGrammarAndSpelling(userContent);
    
    // Find the AI question that led to this user answer
    const sparringQuestion = findPrecedingQuestion(msg);
    
    // Create milestone with enriched meaning and question
    const milestone: GeneratedMilestone = {
      key: template.key,
      title: correctedTitle,
      meaning: correctedMeaning, // Full user response as context (grammar corrected)
      sparringQuestion, // The AI question that led to this answer
      period: template.label,
      targetDate: targetDate.toISOString().split('T')[0],
      selected: true,
    };
    
    // Enrich meaning if we have assessment context
    if (assessmentContext) {
      // Try to find matching decision
      for (const domain of Object.keys(CAPABILITY_DOMAIN_LABELS) as CapabilityDomain[]) {
        const domainKeywords = getDomainKeywords(domain);
        if (domainKeywords.some(kw => userContent.toLowerCase().includes(kw))) {
          const extendedDecision = assessmentContext.extendedDecisions?.find(ed => ed.domain === domain);
          const basicDecision = assessmentContext.capabilityDecisions?.find(d => d.domain === domain);
          
          if (extendedDecision || basicDecision) {
            milestone.meaning = enrichMilestoneMeaning(
              milestone,
              basicDecision || extendedDecision!,
              goal,
              extendedDecision,
              undefined // completeAssessment not available here
            );
            break;
          }
        }
      }
    }
    
    milestones.push(milestone);
    seenTitles.add(normalizedTitle);
    templateIndex++;
  }
  
  return milestones;
}

/**
 * Merge AI summary and user response milestones
 * - Use AI summary for clean titles (if available)
 * - Use user responses for detailed meaning/context
 */
function mergeMilestones(
  summaryMilestones: GeneratedMilestone[],
  userMilestones: GeneratedMilestone[],
  templates: Array<{ key: string; label: string; relativePosition: number; stepKeywords: string[] }>,
  goal: Goal,
  durationDays: number,
  startDate: Date
): GeneratedMilestone[] {
  const merged: GeneratedMilestone[] = [];
  const seenKeys = new Set<string>();
  const seenTitles = new Set<string>(); // Track tekstuele duplicates
  
  // Helper function to normalize title for comparison
  const normalizeTitle = (title: string): string => {
    return title.toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  };
  
  // Helper function to check if two titles are similar (same core content)
  const isSimilarTitle = (title1: string, title2: string): boolean => {
    const norm1 = normalizeTitle(title1);
    const norm2 = normalizeTitle(title2);
    
    // Exact match after normalization
    if (norm1 === norm2) return true;
    
    // Check if titles start with "Start:" or similar prefix
    // If both have the same core after removing prefix, they're duplicates
    const removePrefix = (t: string) => t.replace(/^(start|begin|eerste stap|week \d+):\s*/i, '').trim();
    const core1 = removePrefix(norm1);
    const core2 = removePrefix(norm2);
    if (core1 === core2 && core1.length > 10) return true;
    
    // Check if one contains the other (for partial matches)
    if (norm1.length > 15 && norm2.length > 15) {
      const shorter = norm1.length < norm2.length ? norm1 : norm2;
      const longer = norm1.length >= norm2.length ? norm1 : norm2;
      // If shorter is 70% contained in longer, consider duplicate
      if (longer.includes(shorter.substring(0, Math.floor(shorter.length * 0.7)))) {
        return true;
      }
    }
    
    // Check word overlap (strict)
    const words1 = norm1.split(' ').filter(w => w.length > 3);
    const words2 = norm2.split(' ').filter(w => w.length > 3);
    if (words1.length === 0 || words2.length === 0) return false;
    
    const overlap = words1.filter(w => words2.includes(w)).length;
    const minWords = Math.min(words1.length, words2.length);
    // Require at least 4 words OR 80% overlap (very strict)
    return overlap >= 4 || (minWords > 0 && overlap >= Math.ceil(minWords * 0.8));
  };
  
  // Sort templates by relative position for proper chronological ordering
  const sortedTemplates = [...templates].sort((a, b) => a.relativePosition - b.relativePosition);
  
  // Process each template in chronological order
  for (const template of sortedTemplates) {
    const fromSummary = summaryMilestones.find(m => m.key === template.key);
    const fromUser = userMilestones.find(m => m.key === template.key);
    
    if (fromSummary || fromUser) {
      // Prefer AI summary title (cleaner), fallback to user input
      const title = fromSummary?.title || fromUser?.title || template.label;
      const normalizedTitle = normalizeTitle(title);
      
      // Check for textual duplicates (not just key duplicates)
      const isDuplicate = Array.from(seenTitles).some(seenTitle => 
        isSimilarTitle(seenTitle, normalizedTitle)
      );
      
      if (!isDuplicate) {
        const daysFromStart = Math.round(durationDays * template.relativePosition);
        const targetDate = new Date(startDate.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
        
        // Apply grammar correction to meaning if it comes from user response
        const rawMeaning = fromUser?.meaning || fromSummary?.meaning || `${template.label} voor ${goal.title}`;
        const correctedMeaning = fromUser?.meaning ? correctGrammarAndSpelling(rawMeaning) : rawMeaning;
        
        merged.push({
          key: template.key,
          title: correctGrammarAndSpelling(title), // Also correct title
          // Combine meanings: user response is the real content (grammar corrected)
          meaning: correctedMeaning,
          period: template.label,
          targetDate: targetDate.toISOString().split('T')[0],
          selected: true,
        });
        seenKeys.add(template.key);
        seenTitles.add(normalizedTitle); // Track normalized title
      }
    }
  }
  
  // Add any remaining milestones from summary that weren't in templates
  for (const m of summaryMilestones) {
    if (!seenKeys.has(m.key)) {
      const normalizedTitle = normalizeTitle(m.title);
      const isDuplicate = Array.from(seenTitles).some(seenTitle => 
        isSimilarTitle(seenTitle, normalizedTitle)
      );
      
      if (!isDuplicate) {
        merged.push(m);
        seenKeys.add(m.key);
        seenTitles.add(normalizedTitle);
      }
    }
  }
  
  // Add any remaining from user that weren't matched
  for (const m of userMilestones) {
    if (!seenKeys.has(m.key)) {
      const normalizedTitle = normalizeTitle(m.title);
      const isDuplicate = Array.from(seenTitles).some(seenTitle => 
        isSimilarTitle(seenTitle, normalizedTitle)
      );
      
      if (!isDuplicate) {
        merged.push(m);
        seenKeys.add(m.key);
        seenTitles.add(normalizedTitle);
      }
    }
  }
  
  return merged;
}

/**
 * Extract milestones from AI summary message
 * NOW ENRICHED WITH ASSESSMENT CONTEXT FOR SPECIFICITY
 */
function extractFromSummary(
  content: string,
  templates: Array<{ key: string; label: string; relativePosition: number; stepKeywords: string[] }>,
  goal: Goal,
  durationDays: number,
  startDate: Date,
  assessmentContext?: {
    extendedDecisions?: ExtendedCapabilityDecision[];
    capabilityDecisions?: CapabilityDecision[];
    goalDescription?: string;
  }
): GeneratedMilestone[] {
  const milestones: GeneratedMilestone[] = [];
  
  // Common patterns in AI summaries
  const patterns = [
    // 📅 EINDDOEL: text
    /📅\s*([^:]+):\s*([^\n📅💡🎯]+)/gi,
    // EINDDOEL: text
    /^([A-Z][A-Z\s]+):\s*(.+)$/gm,
    // Over 1 jaar: text
    /(over\s+\d+\s+(?:jaar|maanden?|weken?))[:\s-]+([^\n]+)/gi,
    // Deze week: text
    /(deze\s+week)[:\s-]+([^\n]+)/gi,
  ];

  const found: Record<string, string> = {};
  
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const label = match[1].trim().toLowerCase();
      const value = match[2].trim();
      
      if (value.length > 5) {
        // Match to template
        for (const template of templates) {
          const isMatch = template.stepKeywords.some(kw => 
            label.includes(kw.toLowerCase()) || kw.toLowerCase().includes(label)
          );
          if (isMatch && !found[template.key]) {
            found[template.key] = value;
            break;
          }
        }
      }
    }
  }

  // Convert found items to milestones with assessment enrichment
  for (const template of templates) {
    if (found[template.key]) {
      const daysFromStart = Math.round(durationDays * template.relativePosition);
      const targetDate = new Date(startDate.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
      
      let title = truncateTitle(found[template.key]);
      let meaning = `${template.label}: ${found[template.key]}`;
      
      // Try to enrich with assessment context
      if (assessmentContext) {
        // Check if AI summary mentions a domain
        for (const domain of Object.keys(CAPABILITY_DOMAIN_LABELS) as CapabilityDomain[]) {
          const domainKeywords = getDomainKeywords(domain);
          const mentionsDomain = domainKeywords.some(kw => found[template.key].toLowerCase().includes(kw));
          
          if (mentionsDomain) {
            // Find matching extended decision
            const extendedDecision = assessmentContext.extendedDecisions?.find(ed => ed.domain === domain);
            const basicDecision = assessmentContext.capabilityDecisions?.find(d => d.domain === domain);
            
            if (extendedDecision || basicDecision) {
              // Generate specific title using assessment data
              const assessmentTitle = generateSpecificMilestoneTitle(
                basicDecision || extendedDecision!,
                goal,
                extendedDecision,
                {
                  userResponses: [found[template.key]],
                  goalDescription: assessmentContext.goalDescription
                }
              );
              
              // Use assessment title if it's more specific
              if (assessmentTitle.length > title.length || 
                  (assessmentTitle !== title && assessmentTitle !== template.label)) {
                title = assessmentTitle;
              }
              
              // Enrich meaning
              const milestone: GeneratedMilestone = {
                key: template.key,
                title,
                meaning,
                period: template.label,
                targetDate: targetDate.toISOString().split('T')[0],
                selected: true,
              };
              
              meaning = enrichMilestoneMeaning(
                milestone,
                basicDecision || extendedDecision!,
                goal,
                extendedDecision,
                undefined // completeAssessment not available here
              );
              
              break;
            }
          }
        }
      }
      
      milestones.push({
        key: template.key,
        title,
        meaning,
        period: template.label,
        targetDate: targetDate.toISOString().split('T')[0],
        selected: true,
      });
    }
  }

  return milestones;
}

/**
 * Patterns that indicate conversational/non-goal text
 */
const INVALID_TITLE_PATTERNS = [
  /^ja\b/i,
  /^nee\b/i,
  /^dat is/i,
  /^ik denk/i,
  /^ik weet/i,
  /^ik ga/i,
  /^dat klopt/i,
  /^goed[e]?\s/i,
  /^heel goed/i,
  /^mooi/i,
  /^leuk/i,
  /^interessant/i,
  /^inderdaad/i,
  /^precies/i,
  /^oké/i,
  /^okay/i,
  /^hm+/i,
  /^eh+/i,
  /^nou\b/i,
  /^even\s/i,
  /^weet je/i,
  /^snap je/i,
  /\?$/, // Ends with question mark
  /^dat weet ik/i,
  /^ik snap/i,
  /^begrijp/i,
];

/**
 * Check if text is a valid milestone title
 */
function isValidTitle(text: string): boolean {
  const trimmed = text.trim();
  
  // Too short
  if (trimmed.length < 10) return false;
  
  // Too long (probably a full paragraph)
  if (trimmed.length > 300) return false;
  
  // Matches invalid patterns
  for (const pattern of INVALID_TITLE_PATTERNS) {
    if (pattern.test(trimmed)) return false;
  }
  
  // Contains too many filler words
  const fillerWords = ['eigenlijk', 'misschien', 'wellicht', 'eventueel', 'denk ik', 'weet ik'];
  const fillerCount = fillerWords.filter(w => trimmed.toLowerCase().includes(w)).length;
  if (fillerCount >= 2) return false;
  
  return true;
}

/**
 * Extract the core goal/action from text
 * Removes conversational fluff and extracts the actionable part
 */
function extractCoreGoal(text: string): string | null {
  const trimmed = text.trim();
  
  // Try to find action verbs and extract from there
  const actionPatterns = [
    /(?:ik wil|mijn doel is|ik ga|ik moet|plan is om|bedoeling is)\s+(.+)/i,
    /(?:bereiken|behalen|realiseren|starten|lanceren|bouwen|maken|leren|groeien)\s*[:\-]?\s*(.+)/i,
    /(\d+[.\d]*\s*(?:klanten?|euro|%|procent|mensen|verkopen|omzet|leads?|gebruikers?))/i,
  ];
  
  for (const pattern of actionPatterns) {
    const match = trimmed.match(pattern);
    if (match && match[1] && match[1].length > 10) {
      return match[1].trim();
    }
  }
  
  // If text contains numbers/metrics, it might be a concrete goal
  if (/\d+/.test(trimmed) && trimmed.length < 150) {
    return trimmed;
  }
  
  return null;
}

/**
 * Clean and validate title, with fallback to label
 */
function cleanTitle(text: string, fallbackLabel: string): string {
  // Apply grammar correction first
  let correctedText = correctGrammarAndSpelling(text);
  
  // First try to extract the core goal
  const coreGoal = extractCoreGoal(correctedText);
  if (coreGoal && isValidTitle(coreGoal)) {
    return truncateText(coreGoal);
  }
  
  // Check if original (corrected) is valid
  if (isValidTitle(correctedText)) {
    return truncateText(correctedText);
  }
  
  // Use fallback label
  return fallbackLabel;
}

/**
 * Truncate text to reasonable length
 */
function truncateText(text: string): string {
  // Take first sentence
  const sentences = text.split(/[.!]/);
  const firstSentence = sentences[0].trim();
  
  if (firstSentence.length <= 80) {
    return firstSentence;
  }
  
  // Try to cut at a natural break
  const cutPoints = [' en ', ' of ', ' met ', ' voor ', ' door ', ', '];
  for (const cut of cutPoints) {
    const idx = firstSentence.indexOf(cut, 40);
    if (idx > 40 && idx < 100) {
      return firstSentence.substring(0, idx);
    }
  }
  
  return firstSentence.substring(0, 77) + '...';
}

/**
 * Legacy function - kept for compatibility
 */
function truncateTitle(text: string): string {
  return truncateText(text);
}

/**
 * SparringMilestoneGenerator - Automatisch mijlpalen genereren uit sparring
 * 
 * Flow:
 * 1. Laad capability assessment (indien beschikbaar)
 * 2. Genereer basis mijlpalen uit capability decisions
 * 3. Extract mijlpalen uit AI gesprek
 * 4. Combineer en verrijk capability milestones met AI context
 * 5. Toon als bewerkbare lijst
 * 6. Sla alles op met correcte origin (capability/sparring) en category
 */
const SparringMilestoneGenerator: React.FC<SparringMilestoneGeneratorProps> = ({
  goal,
  ownerId,
  sessionId,
  messages,
  onComplete,
  onCancel,
  onMilestoneCreated,
  entrepreneurContext,
}) => {
  const [capabilityAssessment, setCapabilityAssessment] = useState<CapabilityAssessment | null>(null);
  const [completeAssessment, setCompleteAssessment] = useState<CompleteAssessment | null>(null);
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(true);
  const [learningStyle, setLearningStyle] = useState<LearningStyle | undefined>();

  // Load capability assessment + complete assessment (DISC, Workstyle) + self-test results on mount
  useEffect(() => {
    const loadAssessment = async () => {
      try {
        // Load both: basic capability decisions + full profile (DISC, Workstyle)
        const [basicAssessment, fullAssessment] = await Promise.all([
          getLatestAssessment(ownerId, goal.id),
          getLatestCompleteAssessment(ownerId, goal.id),
        ]);
        
        setCapabilityAssessment(basicAssessment);
        setCompleteAssessment(fullAssessment);
        
        // Load learning style from self-test results (localStorage)
        const selfTestResults = loadSelfTestResults();
        if (selfTestResults?.learningStyle) {
          setLearningStyle(selfTestResults.learningStyle);
          console.log('📚 Learning style loaded:', selfTestResults.learningStyle);
        }
        
        if (fullAssessment?.discProfile) {
          console.log('🎯 DISC Profile loaded:', fullAssessment.discProfile.primary);
        }
        if (fullAssessment?.workstyleProfile) {
          console.log('🛠️ Workstyle Profile loaded:', fullAssessment.workstyleProfile.scores);
        }
      } catch (err) {
        console.error('Failed to load capability assessment:', err);
      } finally {
        setIsLoadingAssessment(false);
      }
    };
    loadAssessment();
  }, [ownerId, goal.id]);

  // Generate capability milestones if assessment exists
  // Also keep mapping of milestone key to capability domain for later use
  const [capabilityDomainMap, setCapabilityDomainMap] = useState<Record<string, CapabilityDomain>>({});
  
  const capabilityMilestones = useMemo(() => {
    if (!capabilityAssessment || !capabilityAssessment.decisions) return undefined;
    
    const capMilestones = generateCapabilityMilestones(capabilityAssessment.decisions);
    const startDate = goal.startDate ? new Date(goal.startDate) : new Date();
    const endDate = goal.endDate ? new Date(goal.endDate) : new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    const durationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Get user responses from messages for context
    const userResponses = messages.filter(m => m.role === 'user').map(m => m.content);
    
    // Convert with extended decisions and all context
    const converted = convertCapabilityToSparringMilestones(
      capMilestones, 
      goal, 
      startDate, 
      durationDays,
      completeAssessment?.extendedDecisions, // Extended decisions for specificity
      completeAssessment || undefined, // Complete assessment for enrichment
      userResponses // User responses for context
    );
    
    // PERSONALIZE each milestone with DISC & Workstyle profiles + Role + Patterns
    const personalized = converted.map(milestone => 
      personalizeMilestoneWithProfile(
        milestone,
        completeAssessment?.discProfile,
        completeAssessment?.workstyleProfile,
        entrepreneurContext?.rolePreference,
        entrepreneurContext?.patterns
      )
    );
    
    // Build domain map
    const domainMap: Record<string, CapabilityDomain> = {};
    capMilestones.forEach((capMilestone) => {
      domainMap[`capability_${capMilestone.domain}`] = capMilestone.domain;
    });
    setCapabilityDomainMap(domainMap);
    
    return personalized;
  }, [capabilityAssessment, completeAssessment, goal, messages]);

  // Generate milestones from conversation + capability assessment
  const initialMilestones = useMemo(
    () => {
      if (isLoadingAssessment) return []; // Wait for assessment to load
      return generateMilestonesFromConversation(
        messages, 
        goal, 
        capabilityMilestones,
        {
          extendedDecisions: completeAssessment?.extendedDecisions,
          capabilityDecisions: capabilityAssessment?.decisions,
          completeAssessment: completeAssessment || undefined
        }
      );
    },
    [messages, goal, capabilityMilestones, isLoadingAssessment, completeAssessment, capabilityAssessment]
  );

  const [milestones, setMilestones] = useState<GeneratedMilestone[]>(initialMilestones);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState(0);

  // Update milestones when initialMilestones change
  useEffect(() => {
    if (!isLoadingAssessment && initialMilestones.length > 0) {
      setMilestones(initialMilestones);
    }
  }, [initialMilestones, isLoadingAssessment]);

  const selectedMilestones = milestones.filter(m => m.selected);

  // Toggle milestone selection
  const toggleMilestone = (key: string) => {
    setMilestones(prev => prev.map(m => 
      m.key === key ? { ...m, selected: !m.selected } : m
    ));
  };

  // Update milestone field
  const updateMilestone = (key: string, field: keyof GeneratedMilestone, value: string) => {
    setMilestones(prev => prev.map(m => 
      m.key === key ? { ...m, [field]: value } : m
    ));
  };

  // Save all selected milestones
  const handleSaveAll = async () => {
    if (isSaving || selectedMilestones.length === 0) return;

    setIsSaving(true);
    setError(null);
    setSavedCount(0);

    let successfulSaves = 0;
    let hasError = false;

    // Global tracker to prevent duplicate actions across milestones
    const seenActionTitles = new Set<string>();
    const normalizeActionTitle = (title: string): string => {
      return title
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    for (let i = 0; i < selectedMilestones.length; i++) {
      const milestone = selectedMilestones[i];
      try {
        // Check if this is a capability-based milestone
        const capabilityDomain = capabilityDomainMap[milestone.key];
        const origin = capabilityDomain ? 'capability' : 'sparring';
        
        // Build milestone data object - filter out undefined values (Firebase doesn't accept undefined)
        const milestoneData: Record<string, unknown> = {
          title: milestone.title,
          targetDate: milestone.targetDate,
          fromSparring: true,
          sparringSessionId: sessionId,
          origin,
        };
        
        // Only add optional fields if they have values
        if (milestone.meaning) {
          milestoneData.meaning = milestone.meaning;
        }
        if (milestone.sparringQuestion) {
          milestoneData.sparringQuestion = milestone.sparringQuestion;
        }
        if (milestone.period) {
          milestoneData.globalPeriod = milestone.period;
        }
        
        // Only add category if it's defined AND maps to a valid value
        if (capabilityDomain) {
          const mappedCategory = mapDomainToCategory(capabilityDomain);
          if (mappedCategory) {
            milestoneData.category = mappedCategory;
          }
        }
        
        // Create milestone
        const milestoneId = await createMilestone(ownerId, goal.id, milestoneData as any);

        // Link to sparring session (non-blocking)
        try {
          await addMilestoneToSession(sessionId, milestoneId);
        } catch (linkError) {
          console.warn('Could not link milestone to session (non-critical):', linkError);
        }
        
        // Notify parent about new milestone (for timeline refresh)
        onMilestoneCreated?.(milestoneId);

        // Generate comprehensive actions for this milestone (non-blocking)
        // Uses ALL available data sources and generates actions for ALL relevant domains
        try {
          // Extract user responses from messages
          const allUserResponses = messages.filter(m => m.role === 'user').map(m => m.content);
          
          // ✅ FIX: Detect the primary domain for this milestone
          const milestoneDomain = detectPrimaryDomainFromMilestone(milestone.title, milestone.meaning || '');
          
          // Build comprehensive context for action generation
          const actionContext = {
            goal,
            milestone: {
              ...milestoneData,
              id: milestoneId,
              ownerId,
              createdAt: Date.now(),
            } as Milestone,
            completeAssessment: completeAssessment || undefined,
            extendedDecisions: completeAssessment?.extendedDecisions,
            sparringMessages: messages,
            userResponses: allUserResponses,
            selfTestResults: entrepreneurContext?.rolePreference ? {
              rolePreference: entrepreneurContext.rolePreference,
              learningStyle: learningStyle, // ✅ Include learning style for action generation
              knowledgeLevels: entrepreneurContext.knowledgeLevels,
            } : learningStyle ? {
              learningStyle: learningStyle, // ✅ Even if no entrepreneur context, include learning style
            } : undefined,
            preSparringAnswers: entrepreneurContext?.preSparringAnswers,
            entrepreneurContext,
            // ✅ FIX: Filter actions to only this milestone's domain
            filterToDomain: milestoneDomain,
          };
          
          // ✅ FIX: Generate actions ONLY for this milestone's domain (not all domains)
          const generatedActions = await generateComprehensiveActions(actionContext);
          
          console.log(`✅ Generated ${generatedActions.length} actions for milestone: ${milestone.title}`);
          console.log(`   Actions per domain:`, 
            Object.entries(
              generatedActions.reduce((acc, a) => {
                acc[a.domain] = (acc[a.domain] || 0) + 1;
                return acc;
              }, {} as Record<string, number>)
            )
          );
          
          // Save all actions to Firebase
          for (const action of generatedActions) {
            try {
              await createAction(ownerId, goal.id, milestoneId, {
                title: action.title,
                completed: false,
                origin: 'milestone_generated',
                priority: action.priority,
                actionType: action.actionType,
                domain: action.domain, // Include domain tag
              });
            } catch (actionErr) {
              console.warn('Failed to create individual action:', actionErr);
              // Continue with next action (non-critical)
            }
          }
          
          // Dispatch custom event to notify other components (e.g., AgendaPage) that actions changed
          window.dispatchEvent(new CustomEvent('doelstellingen-actions-changed'));
        } catch (actionError) {
          console.error('Could not generate actions for milestone (non-critical):', actionError);
        }

        // Log creation (non-blocking)
        try {
          await logMilestoneCreated(ownerId, goal.id, milestoneId, milestone.title);
        } catch (logError) {
          console.warn('Could not log milestone creation (non-critical):', logError);
        }

        successfulSaves++;
        setSavedCount(successfulSaves);
      } catch (err) {
        console.error(`Failed to save milestone "${milestone.title}":`, err);
        hasError = true;
        // Continue with next milestone instead of stopping
      }
    }

    // Done processing all milestones
    setIsSaving(false);

    if (successfulSaves > 0) {
      // At least some milestones were saved - go to completion
      console.log(`✅ Successfully saved ${successfulSaves}/${selectedMilestones.length} milestones`);
      if (hasError) {
        console.warn('Some milestones failed to save, but proceeding with completion');
      }
      
      // Give UI a moment to update, then proceed to close dialog
      setTimeout(() => {
        onComplete(successfulSaves);
      }, 300);
    } else {
      // No milestones were saved - show error
      setError('Kon geen enkele mijlpaal opslaan. Controleer je internetverbinding en probeer opnieuw.');
    }
  };

  // Loading state - show while assessment is being loaded
  if (isLoadingAssessment) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white border-2 border-green-300 rounded-xl p-8 shadow-2xl max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <div className="text-center">
              <h4 className="font-medium text-gray-900 mb-2">Mijlpalen voorbereiden...</h4>
              <p className="text-sm text-gray-600">
                We combineren je gesprek met je profiel om gepersonaliseerde mijlpalen te maken.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - only show if NOT loading and truly no milestones
  if (milestones.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 shadow-2xl max-w-md w-full">
          <div className="flex items-start gap-3">
            <WarningIcon size={24} />
            <div>
              <h4 className="font-medium text-yellow-800">Geen mijlpalen gevonden</h4>
              <p className="text-sm text-yellow-700 mt-1">
                We konden geen mijlpalen extraheren uit het gesprek. 
                Ga terug en zorg dat je concrete doelen hebt besproken.
              </p>
              <button
                onClick={onCancel}
                className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium w-full"
              >
                ← Terug naar gesprek
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Saving state
  if (isSaving) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-8 text-center shadow-2xl max-w-md w-full">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mb-4"></div>
          <h4 className="font-bold text-lg text-gray-900">Mijlpalen en acties aanmaken...</h4>
          <p className="text-sm text-gray-600 mt-2">
            {savedCount} van {selectedMilestones.length} voltooid
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Voor elke mijlpaal wordt automatisch een actie aangemaakt
          </p>
          <div className="w-full bg-green-200 rounded-full h-3 mt-4 shadow-inner">
            <div 
              className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(savedCount / selectedMilestones.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div 
        className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90dvh] flex flex-col"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
        {/* Phase header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 text-white flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <GoalIcon size={32} />
            <div>
              <h3 className="text-xl font-bold">Mijlpalen Genereren</h3>
              <p className="text-sm text-green-100">
                Fase 4: Automatisch gegenereerd uit je sparring gesprek
              </p>
            </div>
          </div>
          {/* Profile indicators */}
          <div className="flex flex-col gap-1.5">
            {completeAssessment?.discProfile && (
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs backdrop-blur-sm">
                <span>🎨</span>
                <span className="font-medium">DISC: {
                  completeAssessment.discProfile.primary === 'red' ? '🔴 Dominant' : 
                  completeAssessment.discProfile.primary === 'yellow' ? '🟡 Invloedrijk' : 
                  completeAssessment.discProfile.primary === 'green' ? '🟢 Stabiel' : '🔵 Consciëntieus'
                }</span>
              </div>
            )}
            {completeAssessment?.workstyleProfile && (
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs backdrop-blur-sm">
                <SettingsIcon size={16} />
                <span className="font-medium">Werkstijl: {
                  completeAssessment.workstyleProfile.primaryStrength || 'Afgestemd'
                }</span>
              </div>
            )}
            {!completeAssessment?.discProfile && !completeAssessment?.workstyleProfile && capabilityAssessment && (
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs backdrop-blur-sm">
                <AnalyticsIcon size={16} />
                <span className="font-medium">Basis assessment</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection header */}
      <div className="bg-green-50 border-b border-green-100 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">
              Selecteer de mijlpalen die je wilt opslaan
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {selectedMilestones.length} van {milestones.length} geselecteerd
            </p>
            {(completeAssessment?.discProfile || completeAssessment?.workstyleProfile) && (
              <p className="text-xs text-blue-600 mt-1">
                Mijlpalen zijn gepersonaliseerd op basis van jouw profiel
              </p>
            )}
          </div>
          <button
            onClick={handleSaveAll}
            disabled={selectedMilestones.length === 0 || isSaving}
            className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isSaving ? <><HourglassIcon size={14} className="inline" /> Opslaan... ({savedCount}/{selectedMilestones.length})</> : `✓ Opslaan (${selectedMilestones.length})`}
          </button>
        </div>
      </div>

      {/* Milestone list */}
      <div className="divide-y divide-gray-100 overflow-y-auto flex-1 scrollbar-always">
        {milestones.map((milestone, index) => (
          <div 
            key={milestone.key}
            className={`p-4 transition-colors ${milestone.selected ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
          >
            <div className="flex items-start gap-3">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={milestone.selected}
                onChange={() => toggleMilestone(milestone.key)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />

              {/* Number badge */}
              <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold
                ${milestone.selected ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'}`}>
                {index + 1}
              </span>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Period label */}
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                  ${milestone.selected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>
                  {milestone.period}
                </span>

                {/* Title - editable */}
                <input
                  type="text"
                  value={milestone.title}
                  onChange={(e) => updateMilestone(milestone.key, 'title', e.target.value)}
                  disabled={!milestone.selected}
                  className={`w-full mt-2 px-2 py-1 text-sm font-medium border rounded
                    ${milestone.selected 
                      ? 'border-gray-200 focus:border-orange-300 focus:ring-1 focus:ring-orange-200' 
                      : 'border-transparent bg-transparent'}`}
                />

                {/* Target date */}
                <div className="flex items-center gap-2 mt-2">
                  <CalendarIcon size={12} className="text-gray-500" />
                  <input
                    type="date"
                    value={milestone.targetDate}
                    onChange={(e) => updateMilestone(milestone.key, 'targetDate', e.target.value)}
                    disabled={!milestone.selected}
                    className={`text-xs px-2 py-1 border rounded
                      ${milestone.selected 
                        ? 'border-gray-200 focus:border-orange-300' 
                        : 'border-transparent bg-transparent text-gray-400'}`}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border-t border-red-100 flex-shrink-0">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Terug
        </button>
        <p className="text-xs text-gray-400">
          Je kunt mijlpalen later nog aanpassen
        </p>
      </div>
      </div>
    </div>
  );
};

export default SparringMilestoneGenerator;
